import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, memo } from 'react';
import { Send, Square, Paperclip, FileText, Sparkles, ChevronDown, ChevronRight, Loader2, BookOpen, Scale, FileCheck, Copy, Trash2, X, ThumbsUp, ThumbsDown, Plus, Edit, Type, Download, Settings, Search, Languages, Database, File, FileEdit, Save, RefreshCw, Image as ImageIcon, Check } from 'lucide-react';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } from 'docx';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useSessionStore, useNotificationStore, useUserStore } from '@/stores';
import { useTaskResultCopy } from '@/hooks/useTaskResultCopy';
import { useCompactMode } from '@/hooks/useCompactMode';
import { usePremium } from '@/hooks/usePremium';
import { useChatFontSize, FONT_SIZE_OPTIONS } from '@/hooks/useChatFontSize';
import { useResponseDetailLevel, RESPONSE_DETAIL_OPTIONS } from '@/hooks/useResponseDetailLevel';
import { useChatModel, CHAT_MODEL_OPTIONS } from '@/hooks/useChatModel';
import { useChatLanguage, LANGUAGE_OPTIONS } from '@/hooks/useChatLanguage';
import Markdown from 'markdown-to-jsx';
import { formatResourceDisplay, stripResourceTags } from '@/utils/text';
import { preprocessAssistantMarkdown } from '@/utils/messageMarkdown';
import { exportDraftToDocx } from '@/utils/draftExport';
import { chatApi, userApi, documentApi, resourceApi, draftApi, type ChatMessage } from '@/services/api';
import { API_CONFIG } from '@/config/api.config';
import { MessageContext } from './MessageContext';
import ChatDrawing from './ChatDrawing';
import { PENDING_MSG_KEY as PENDING_MESSAGE_KEY, forgetPendingMessage } from '@/utils/pendingMessage';
import ChatDrawingAsk from './ChatDrawingAsk';
import { FeedbackModal } from './FeedbackModal';
import { CustomPromptModal } from './CustomPromptModal';
import { DraftModificationDialog } from './DraftModificationDialog';
import { LineNumberedTextarea } from './LineNumberedTextarea';
import { useDraftModification } from '@/hooks/useDraftModification';
import type { TaskResultAttachment } from '@/stores/sessionStore';
import { LexicalChatInput } from './lexical';
import { ChatSettingsMenu } from './ChatSettingsMenu';
import PartsListDisplay from './task-displays/PartsListDisplay';
import SpecMapDisplay from './task-displays/SpecMapDisplay';
import ClaimsSupportDisplay from './task-displays/ClaimsSupportDisplay';
import ClaimsWithNumeralsDisplay from './task-displays/ClaimsWithNumeralsDisplay';
import CustomAnalysisDisplay from './task-displays/CustomAnalysisDisplay';
import IRMetadataDisplay from './task-displays/IRMetadataDisplay';
import IRInnovationAnalysisDisplay from './task-displays/IRInnovationAnalysisDisplay';
import IRTechClassificationDisplay from './task-displays/IRTechClassificationDisplay';
import DraftSummaryDisplay from './task-displays/DraftSummaryDisplay';
import DraftDetailedDescriptionDisplay from './task-displays/DraftDetailedDescriptionDisplay';
import DraftBackgroundDisplay from './task-displays/DraftBackgroundDisplay';
import DraftClaimsDisplay from './task-displays/DraftClaimsDisplay';
import OtherEmbodimentsDisplay from './task-displays/OtherEmbodimentsDisplay';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import TranslateDocumentDisplay from './task-displays/TranslateDocumentDisplay';

interface ChatAreaProps {
  activePage: 'chat' | 'documents' | 'examination';
  onDocumentClick: (doc: any) => void;
  sidebarCollapsed?: boolean;
  hasOpenDocuments?: boolean;
  onOpenDocumentModal?: () => void;
  visible?: boolean;
}

interface AssistantMessageProps {
  msg: any;
  compactMode: boolean;
  fontSizeStyle: React.CSSProperties;
  messageFeedback: Record<number, 'positive' | 'negative'>;
  feedbackToasts: Record<number, string>;
  hoveredMessageId: number | null;
  onDocumentClick: (doc: any) => void;
  preprocessAssistantMessage: (text: string, messageId?: number) => string;
  markdownOverrides: any;
  onThumbsUp: (content: string, msgId: number) => void;
  onThumbsDown: (content: string, msgId: number) => void;
  onDeleteMessage: (msgId: number) => void;
  onCopyMessage: (content: string, messageId?: number) => void;
  copiedMessageId: number | null;
  setHoveredMessageId: (id: number | null) => void;
  activeDraft: any;
  activeDraftsInChat: any[];
  setPendingDraftContent: (content: string) => void;
  setShowDraftModDialog: (show: boolean) => void;
  stripResourceTags: (text: string) => string;
  searchQuery: string;
  isCurrentSearchResult: boolean;
  /** A drawing this answer offered, once something has happened to it. */
  onImageProposal: (msgId: number, proposal: any) => void;
}

// Memoized assistant message component to prevent unnecessary re-renders
const AssistantMessage = memo(({
  msg,
  compactMode,
  fontSizeStyle,
  messageFeedback,
  feedbackToasts,
  hoveredMessageId,
  onDocumentClick,
  preprocessAssistantMessage,
  markdownOverrides,
  onThumbsUp,
  onThumbsDown,
  onDeleteMessage,
  onCopyMessage,
  copiedMessageId,
  setHoveredMessageId,
  activeDraft,
  activeDraftsInChat,
  setPendingDraftContent,
  setShowDraftModDialog,
  stripResourceTags,
  searchQuery,
  isCurrentSearchResult,
  onImageProposal,
}: AssistantMessageProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    // Remove previous highlights
    container.querySelectorAll('span.search-highlight').forEach(el => {
      const parent = el.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(el.textContent || ''), el);
        parent.normalize();
      }
    });

    if (!searchQuery.trim()) return;

    const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedQuery, 'gi');
    const highlightClass = isCurrentSearchResult
      ? 'search-highlight bg-yellow-300 text-gray-900 font-semibold'
      : 'search-highlight bg-yellow-100 text-gray-900';

    // Collect text nodes first (walker becomes invalid when DOM changes)
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) textNodes.push(node as Text);

    textNodes.forEach(textNode => {
      const text = textNode.textContent || '';
      if (!regex.test(text)) { regex.lastIndex = 0; return; }
      regex.lastIndex = 0;

      const frag = document.createDocumentFragment();
      let last = 0, match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        if (match.index > last) frag.appendChild(document.createTextNode(text.slice(last, match.index)));
        const span = document.createElement('span');
        span.className = highlightClass;
        span.textContent = match[0];
        frag.appendChild(span);
        last = match.index + match[0].length;
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      textNode.parentNode?.replaceChild(frag, textNode);
    });
  }, [searchQuery, isCurrentSearchResult]);

  return (
    <div
      className={`flex justify-start group ${compactMode ? 'gap-1' : 'gap-2'}`}
      onMouseEnter={() => {
        const selection = window.getSelection();
        if (!selection || selection.toString().length === 0) {
          setHoveredMessageId(msg.id);
        }
      }}
      onMouseLeave={() => {
        const selection = window.getSelection();
        if (!selection || selection.toString().length === 0) {
          setHoveredMessageId(null);
        }
      }}
    >
      <div className={`max-w-[800px] text-gray-900 rounded-2xl rounded-tl-sm ${compactMode ? 'px-1.5 py-1.5' : 'px-2 py-2'} ${
        messageFeedback[msg.id] === 'positive'
          ? 'bg-green-50 border-2 border-green-300'
          : messageFeedback[msg.id] === 'negative'
          ? 'bg-red-50 border-2 border-red-300'
          : 'bg-gray-100'
      }`}>
        {msg.message_context && (
          <MessageContext
            messageContext={msg.message_context}
            sessionContext={msg.session_context}
            onDocumentClick={onDocumentClick}
            compactMode={compactMode}
          />
        )}

        <div ref={contentRef} className={`prose chat-markdown select-text ${compactMode ? 'px-1.5 pt-0.5' : 'px-2 pt-1'}`} style={fontSizeStyle}>
          <Markdown options={{ overrides: markdownOverrides }}>
            {preprocessAssistantMessage(msg.content, msg.id)}
          </Markdown>
        </div>

        {/* The drawing made for this answer. Confirmed before the turn did any work —
            see ChatDrawingAsk and `rag/image_requests.py` — so this only waits for it. */}
        {(msg as any).image_proposal && (msg as any).userMessageId && (
          <ChatDrawing
            messageId={(msg as any).userMessageId}
            drawing={(msg as any).image_proposal}
            onChange={(drawing) => onImageProposal(msg.id, drawing)}
          />
        )}

        {msg.token_usage && msg.token_usage.model_fallback_from && (
          <div className="px-2 pt-0.5 text-[11px] italic text-amber-600 dark:text-amber-500">
            ⚠️ The selected model ({CHAT_MODEL_OPTIONS.find(opt => opt.value === msg.token_usage.model_fallback_from)?.label || msg.token_usage.model_fallback_from}) was unavailable, so this response was generated with {CHAT_MODEL_OPTIONS.find(opt => opt.value === msg.token_usage.model)?.label || msg.token_usage.model || 'GPT-4o'} instead.
          </div>
        )}

        {msg.documents && (
          <div className="space-y-2 pl-4">
            {msg.documents.map((doc: any, idx: number) => (
              <div
                key={idx}
                onClick={() => onDocumentClick(doc)}
                className="bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 mb-1">{doc.title}</div>
                    <div className="text-xs text-gray-500">{doc.type}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-gray-500 pl-2">
          <span title={`This message was generated at ${msg.timestamp}.`}>{msg.timestamp}</span>
          {msg.token_usage && msg.token_usage.credits_used !== undefined && (
            <>
              <span>•</span>
              <span title={`This message incurred ${msg.token_usage.credits_used} credit${msg.token_usage.credits_used !== 1 ? 's' : ''}.`} className="font-medium text-gray-700">{msg.token_usage.credits_used} credit{msg.token_usage.credits_used !== 1 ? 's' : ''}</span>
            </>
          )}
          {msg.token_usage && msg.token_usage.model && (
            <>
              <span>•</span>
              <span title={`This response was generated using the ${msg.token_usage.model} model.`} className="font-medium text-gray-700">{CHAT_MODEL_OPTIONS.find(opt => opt.value === msg.token_usage.model)?.label || msg.token_usage.model}</span>
            </>
          )}
          {feedbackToasts[msg.id] && (
            <div className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium border border-green-200">
              {feedbackToasts[msg.id]}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-start gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
          onClick={() => onThumbsUp(msg.content, msg.id)}
          title="Send positive feedback for this response"
        >
          <ThumbsUp className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600"
          onClick={() => onThumbsDown(msg.content, msg.id)}
          title="Send negative feedback for this response"
        >
          <ThumbsDown className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`${copiedMessageId === msg.id ? 'h-auto px-2' : 'h-8 w-8 p-0'} hover:bg-gray-100`}
          onClick={() => onCopyMessage(msg.content, msg.id)}
          title="Copy message"
        >
          {copiedMessageId === msg.id ? (
            <span className="text-xs text-green-600 font-medium">Copied</span>
          ) : (
            <Copy className="w-4 h-4 text-gray-600" />
          )}
        </Button>
        {(activeDraft || activeDraftsInChat.length > 0) && (
          <>
            <Separator orientation="vertical" className="h-6" />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 gap-2 hover:bg-purple-50 hover:text-purple-600"
              onClick={() => {
                setPendingDraftContent(stripResourceTags(msg.content));
                setShowDraftModDialog(true);
              }}
              title="Add this response to active draft"
            >
              <FileEdit className="w-4 h-4" />
              Add to Draft
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-red-100"
          onClick={() => onDeleteMessage(msg.id)}
          title="Delete message"
        >
          <Trash2 className="w-4 h-4 text-red-600" />
        </Button>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if message content or key display props change
  return (
    prevProps.msg.id === nextProps.msg.id &&
    prevProps.msg.content === nextProps.msg.content &&
    prevProps.compactMode === nextProps.compactMode &&
    prevProps.fontSizeStyle.fontSize === nextProps.fontSizeStyle.fontSize &&
    prevProps.messageFeedback[prevProps.msg.id] === nextProps.messageFeedback[nextProps.msg.id] &&
    prevProps.activeDraft === nextProps.activeDraft &&
    prevProps.activeDraftsInChat.length === nextProps.activeDraftsInChat.length &&
    prevProps.searchQuery === nextProps.searchQuery &&
    // A drawing this answer offered, which changes AFTER the answer is complete: queued,
    // being drawn, drawn, turned down. Compared by reference, which is exactly right
    // here — the store replaces the object on every change and leaves it alone
    // otherwise. Without this line the card never repainted: the request went out, the
    // worker drew the figure, and the chat sat there showing the button.
    prevProps.msg.image_proposal === nextProps.msg.image_proposal &&
    prevProps.isCurrentSearchResult === nextProps.isCurrentSearchResult
  );
});

/**
 * An image attached to a message, with its download control.
 *
 * Its own component for one reason: the control hangs over the image, so it must not be
 * offered until the image is on the page. Hovering a box that is still loading produced a
 * download button that fetched nothing, and an image that FAILED hid itself and left the
 * button behind — a control floating over empty space. Knowing whether the browser has
 * the image needs state, and state needs a component.
 */
const AttachedImage = ({ s3Key }: { s3Key: string }) => {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);

  const url = `${API_CONFIG.baseUrl}/chat/image/${s3Key}?id_token=${encodeURIComponent(localStorage.getItem('id_token') || '')}`;

  const download = () => {
    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        const href = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.download = s3Key.split('/').pop() || 'image.jpg';
        document.body.appendChild(anchor);
        anchor.click();
        window.URL.revokeObjectURL(href);
        document.body.removeChild(anchor);
      })
      .catch(err => console.error('Failed to download image:', err));
  };

  // Nothing to show and nothing to act on.
  if (broken) return null;

  return (
    <div className="mt-2 relative group inline-block">
      <img
        src={url}
        alt="User attached image"
        className="max-h-60 rounded-lg border border-white/30"
        onLoad={() => setLoaded(true)}
        onError={() => {
          console.error('Failed to load image:', s3Key);
          setBroken(true);
        }}
      />
      {/* Download button — on hover, and only once there is an image under it. */}
      {loaded && (
        <button
          onClick={download}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white p-2 rounded-lg"
          title="Download image"
        >
          <Download className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Custom component for resource links
const ResourceLink = ({ children, onDocumentClick, activeDocuments, ...props }: any) => {
  const { 'data-num-type': numType, 'data-clean-num': cleanNum, 'data-rsrc-type': rsrcType, 'data-rsrc-id': rsrcId } = props;

  // Handle case with num-type and clean-num (old format)
  if (cleanNum && numType && rsrcType && rsrcId) {
    // Use children if available (for grouped tags), otherwise generate display text
    const displayText = children || formatResourceDisplay(rsrcType, rsrcId, cleanNum, numType);

    const handleClick = () => {
      if (onDocumentClick) {
        const titleCaseType = ['cfr', 'mpep', 'usc', 'epo', 'wipo', 'uspto'].includes(rsrcType.toLowerCase())
          ? rsrcType.toUpperCase()
          : rsrcType.charAt(0).toUpperCase() + rsrcType.slice(1).toLowerCase();

        // Determine the tab and scroll position based on numType
        let initialTab;
        let scrollToId;
        if (numType === 'paragraph') {
          initialTab = 'specification';
          scrollToId = `p-${cleanNum}`;
        } else if (numType === 'claim') {
          initialTab = 'claims';
          scrollToId = `claim-${cleanNum}`;
        }

        onDocumentClick({
          id: rsrcId,
          type: titleCaseType,
          title: `${titleCaseType} ${rsrcId}`,
          initialTab,
          scrollToId
        });
      }
    };

    const colorClass = rsrcType === 'patent'
      ? 'bg-teal-100 text-teal-900 hover:bg-teal-200'
      : rsrcType === 'publication'
      ? 'bg-blue-100 text-blue-900 hover:bg-blue-200'
      : 'bg-gray-100 text-gray-900 hover:bg-gray-200';

    // Grouped tags (with parentheses and colon like "(Application 123: Claims 1, 2)") have no horizontal padding
    // Children can be an array, so check first element if array
    const childText = Array.isArray(children) ? children[0] : children;
    const isGrouped = typeof childText === 'string' && childText.startsWith('(');
    const paddingClass = isGrouped ? 'px-0' : 'px-2';

    return (
      <span
        title={`Click to view ${typeof displayText === 'string' ? displayText : rsrcId}`}
        className={`inline-flex items-center ${colorClass} ${paddingClass} py-0.5 rounded text-xs font-medium cursor-pointer transition-colors not-prose`}
        onClick={handleClick}
      >
        {displayText}
      </span>
    );
  }

  // Handle case with only rsrc-type and rsrc-id (new format from <Rsrc /> tags)
  if (rsrcType && rsrcId) {
    const docs = activeDocuments || [];
    const uploads = docs.filter((d: any) => d.type === 'Upload');

    console.log('[ResourceLink] All session documents:', docs);
    console.log('[ResourceLink] All uploads:', uploads);
    console.log('[ResourceLink] Looking for rsrcId:', rsrcId);

    // Find the document by rsrc_id - try multiple field matches
    const doc = docs.find((d: any) =>
      d.id === rsrcId ||
      d.resource_id === rsrcId ||
      d.title === rsrcId ||
      // For uploads, check if the ID contains or matches the hash
      (d.type === 'Upload' && (
        d.id?.includes(rsrcId) ||
        rsrcId?.includes(d.id || '') ||
        d.resource_id?.includes(rsrcId) ||
        rsrcId?.includes(d.resource_id || '')
      ))
    );

    console.log('[ResourceLink] Found document:', doc);

    const titleCaseType = rsrcType.charAt(0).toUpperCase() + rsrcType.slice(1).toLowerCase();
    const displayText = doc?.title || doc?.file_name || rsrcId;

    const handleClick = () => {
      if (onDocumentClick) {
        onDocumentClick({
          id: rsrcId,
          type: titleCaseType,
          title: displayText,
          resource_id: rsrcId
        });
      }
    };

    // Color code based on type
    const colorClass = rsrcType.toLowerCase() === 'upload'
      ? 'bg-amber-100 text-amber-900 hover:bg-amber-200'
      : rsrcType.toLowerCase() === 'patent'
      ? 'bg-teal-100 text-teal-900 hover:bg-teal-200'
      : rsrcType.toLowerCase() === 'publication'
      ? 'bg-blue-100 text-blue-900 hover:bg-blue-200'
      : 'bg-gray-100 text-gray-900 hover:bg-gray-200';

    // Grouped tags (with parentheses and colon like "(Application 123: Claims 1, 2)") have no horizontal padding
    // Children can be an array, so check first element if array
    const childText = Array.isArray(children) ? children[0] : children;
    const isGrouped = typeof childText === 'string' && childText.startsWith('(');
    const paddingClass = isGrouped ? 'px-0' : 'px-2';

    return (
      <span
        title={`Click to view ${displayText}`}
        className={`inline-flex items-center ${colorClass} ${paddingClass} py-0.5 rounded text-xs font-medium cursor-pointer transition-colors not-prose`}
        onClick={handleClick}
      >
        {children || displayText}
      </span>
    );
  }

  return <span>{children}</span>;
};

// Helper function to convert document type to title case
const toTitleCaseDocType = (docType: string): string => {
  if (!docType) return docType;

  // Special handling for compound types
  if (docType.toLowerCase() === 'pros_doc') {
    return 'Prosecution Doc';
  }
  if (docType.toLowerCase() === 'foreign_patent') {
    return 'Foreign Patent';
  }

  // Keep certain acronyms in all caps
  const upperCaseTypes = ['cfr', 'mpep', 'usc', 'epo', 'wipo', 'uspto', 'upload'];
  const lowerDocType = docType.toLowerCase();

  if (upperCaseTypes.includes(lowerDocType)) {
    return docType.toUpperCase();
  }

  // Otherwise use title case (capitalize first letter)
  return docType.charAt(0).toUpperCase() + docType.slice(1).toLowerCase();
};

export function ChatArea({ activePage, onDocumentClick, sidebarCollapsed = false, hasOpenDocuments = false, onOpenDocumentModal, visible = true }: ChatAreaProps) {
  const message = useSessionStore(state => state.chatInput);
  const setMessage = useSessionStore(state => state.setChatInput);
  const sendPendingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [processingStatuses, setProcessingStatuses] = useState<Array<{status: string; message: string; timestamp: number; documents?: string[]; resources?: string[]; classification?: string[]}>>([]);
  const [completedStatusHistories, setCompletedStatusHistories] = useState<Map<string, Array<{status: string; message: string; timestamp: number; documents?: string[]; resources?: string[]; classification?: string[]}>>>(new Map());
  const [expandedStatusHistories, setExpandedStatusHistories] = useState<Set<string>>(new Set());
  const [showDelayMessage, setShowDelayMessage] = useState(false);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<number | null>(null);
  const [selectedTaskResult, setSelectedTaskResult] = useState<TaskResultAttachment | null>(null);
  const [showTaskSidePane, setShowTaskSidePane] = useState(false);
  const [feedbackToasts, setFeedbackToasts] = useState<Record<number, string>>({});
  const [messageFeedback, setMessageFeedback] = useState<Record<number, 'positive' | 'negative'>>({});
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showChatInfoModal, setShowChatInfoModal] = useState(false);
  const [customPrompts, setCustomPrompts] = useState<Array<{ prompt_id: string; label: string; prompt: string; created_at: string; available_in?: string }>>([]);
  const [showCustomPromptModal, setShowCustomPromptModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<{ id: string; label: string; prompt: string; available_in?: string } | null>(null);
  const [showQuickPromptsMenu, setShowQuickPromptsMenu] = useState(false);

  // Search state
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState(''); // What user types
  const [searchQuery, setSearchQuery] = useState(''); // What we actually search/highlight
  const [searchResults, setSearchResults] = useState<Array<{messageId: number, content: string, type: string, timestamp: string}>>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [showSearchWarning, setShowSearchWarning] = useState(false);
  const [pendingSearchQuery, setPendingSearchQuery] = useState('');
  const [pendingMatchCount, setPendingMatchCount] = useState(0);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!visible) return;
    const handleSearchShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setShowSearchDialog(true);
      }
      if (event.key === 'Escape') setShowSearchDialog(false);
    };
    window.addEventListener('keydown', handleSearchShortcut);
    return () => window.removeEventListener('keydown', handleSearchShortcut);
  }, [visible]);

  // Document selection panel state
  const [showDocumentPanel, setShowDocumentPanel] = useState(false);

  // Draft panel state
  const [showDraftPanel, setShowDraftPanel] = useState(false);
  const [activeDraft, setActiveDraft] = useState<any>(null);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [draftContent, setDraftContent] = useState('');
  const [draftTitle, setDraftTitle] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [showDraftModDialog, setShowDraftModDialog] = useState(false);
  const [pendingDraftContent, setPendingDraftContent] = useState('');
  const [selectedDraftLine, setSelectedDraftLine] = useState<number | undefined>(undefined);
  const { modifyDraft } = useDraftModification();
  const [draftWidth, setDraftWidth] = useState(500); // Default size in pixels (width when horizontal, height when vertical)
  const [isDraggingResize, setIsDraggingResize] = useState(false);
  const [compactButtons, setCompactButtons] = useState(false);
  const chatInputContainerRef = useRef<HTMLDivElement>(null);

  const [activeDraftsInChat, setActiveDraftsInChat] = useState<any[]>(() => {
    // Load active drafts from localStorage on mount
    try {
      const stored = localStorage.getItem('active_drafts');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Image attachment state
  const [attachedImage, setAttachedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Track copied messages for showing "Copied" indicator
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);

  // Listen for clear-active-drafts event (triggered when starting new session)
  useEffect(() => {
    const handleClearActiveDrafts = () => {
      console.log('[ChatArea] Clearing active drafts due to new session');
      setActiveDraftsInChat([]);
    };

    window.addEventListener('clear-active-drafts', handleClearActiveDrafts);
    return () => window.removeEventListener('clear-active-drafts', handleClearActiveDrafts);
  }, []);

  // Sync activeDraftsInChat to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('active_drafts', JSON.stringify(activeDraftsInChat));
    } catch (error) {
      console.error('Failed to save active drafts to localStorage:', error);
    }
  }, [activeDraftsInChat]);

  // Handle draft resize dragging
  useEffect(() => {
    if (!isDraggingResize) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (hasOpenDocuments) {
        // Vertical resizing when documents are open
        const newHeight = window.innerHeight - e.clientY;
        const minChatHeight = 300;
        const maxDraftHeight = window.innerHeight - minChatHeight;
        const minDraftHeight = 200;
        const constrainedHeight = Math.max(minDraftHeight, Math.min(newHeight, maxDraftHeight));
        setDraftWidth(constrainedHeight);
      } else {
        // Horizontal resizing when no documents are open
        const newWidth = window.innerWidth - e.clientX;
        const minChatWidth = sidebarCollapsed ? 700 : 780;
        const maxDraftWidth = window.innerWidth - minChatWidth;
        const minDraftWidth = 300;
        const constrainedWidth = Math.max(minDraftWidth, Math.min(newWidth, maxDraftWidth));
        setDraftWidth(constrainedWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingResize(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingResize, hasOpenDocuments, sidebarCollapsed]);

  // MPEP/CFR/USC state for document panel
  const [mpepSectionInput, setMpepSectionInput] = useState('');
  const [mpepStructure, setMpepStructure] = useState<any>(null);
  const [loadingMpepSection, setLoadingMpepSection] = useState(false);
  const [loadingMpepStructure, setLoadingMpepStructure] = useState(false);
  const [expandedMpepChapters, setExpandedMpepChapters] = useState<Set<string>>(new Set());

  const [cfrSectionInput, setCfrSectionInput] = useState('');
  const [cfrStructure, setCfrStructure] = useState<any>(null);
  const [loadingCfrSection, setLoadingCfrSection] = useState(false);
  const [loadingCfrStructure, setLoadingCfrStructure] = useState(false);
  const [expandedCfrParts, setExpandedCfrParts] = useState<{ [key: string]: boolean }>({});
  const [expandedCfrSubparts, setExpandedCfrSubparts] = useState<{ [key: string]: boolean }>({});

  // Load all documents state
  const [allPatentDocs, setAllPatentDocs] = useState<any[]>([]);
  const [patientsLoaded, setPatientsLoaded] = useState(false);
  const [loadingPatents, setLoadingPatents] = useState(false);

  const [allPublicationDocs, setAllPublicationDocs] = useState<any[]>([]);
  const [publicationsLoaded, setPublicationsLoaded] = useState(false);
  const [loadingPublications, setLoadingPublications] = useState(false);

  const [allUploadDocs, setAllUploadDocs] = useState<any[]>([]);
  const [uploadsLoaded, setUploadsLoaded] = useState(false);
  const [loadingUploads, setLoadingUploads] = useState(false);

  const [allForeignPatentDocs, setAllForeignPatentDocs] = useState<any[]>([]);
  const [foreignPatientsLoaded, setForeignPatientsLoaded] = useState(false);
  const [loadingForeignPatents, setLoadingForeignPatents] = useState(false);

  // Filter custom prompts for chat context (only chat or both)
  const chatPrompts = useMemo(() => {
    return customPrompts.filter(p =>
      !p.available_in || p.available_in === 'chat' || p.available_in === 'both'
    );
  }, [customPrompts]);

  const addNotification = useNotificationStore(state => state.addNotification);
  const { showToast, copyResult } = useTaskResultCopy();
  const chatMessages = useSessionStore(state => state.chatMessages);
  const sessionId = useSessionStore(state => state.sessionId);
  const sessionDocuments = useSessionStore(state => state.sessionDocuments);
  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const selectedApplicationNumber = useSessionStore(state => state.selectedApplicationNumber);
  const selectedAppData = useSessionStore(state => state.selectedAppData);
  const sessionContext = useSessionStore(state => state.sessionContext);
  const taskAttachments = useSessionStore(state => state.taskAttachments);
  const sentTaskResults = useSessionStore(state => state.sentTaskResults);
  const addChatMessage = useSessionStore(state => state.addChatMessage);
  const updateChatMessage = useSessionStore(state => state.updateChatMessage);
  const deleteChatMessage = useSessionStore(state => state.deleteChatMessage);
  const removeTaskAttachment = useSessionStore(state => state.removeTaskAttachment);
  const clearTaskAttachments = useSessionStore(state => state.clearTaskAttachments);
  const storeSentTaskResults = useSessionStore(state => state.storeSentTaskResults);
  const setSessionContext = useSessionStore(state => state.setSessionContext);
  const autoSaveSession = useSessionStore(state => state.autoSaveSession);
  const autoSaveRegularSession = useSessionStore(state => state.autoSaveRegularSession);
  const userSettings = useUserStore(state => state.userSettings);
  const { compactMode } = useCompactMode();
  const { isPremium } = usePremium();
  const { chatFontSize, setChatFontSize, fontSizeStyle } = useChatFontSize();
  const { chatLanguage, setChatLanguage } = useChatLanguage();
  const { responseDetailLevel, setResponseDetailLevel } = useResponseDetailLevel();
  const { chatModel, setChatModel } = useChatModel();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const delayMessageTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const queuedStatusTimestampsRef = useRef<Set<number>>(new Set()); // Track which statuses have been queued
  // Named in `utils/pendingMessage` because starting a new session has to forget it too.
  const PENDING_MSG_KEY = PENDING_MESSAGE_KEY;
  // A turn that stopped to ask whether to draw something. It is not a message and never
  // becomes one: nothing has been fetched and no answer written, so this sits where the
  // thinking indicator would be until the attorney answers.
  const [drawingAsk, setDrawingAsk] = useState<{ messageId: string; drawing: any } | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  // Sync activeDraftsInChat and sessionContext with localStorage when storage-update event fires
  useEffect(() => {
    const handleStorageUpdate = () => {
      try {
        // Sync active drafts
        const stored = localStorage.getItem('active_drafts');
        const drafts = stored ? JSON.parse(stored) : [];
        setActiveDraftsInChat(drafts);

        // Sync session context
        const sessionContextStr = localStorage.getItem('session_context');
        if (sessionContextStr) {
          const context = JSON.parse(sessionContextStr);
          setSessionContext(context);
        }
      } catch (error) {
        console.error('Failed to sync from localStorage:', error);
      }
    };

    window.addEventListener('storage-update', handleStorageUpdate);
    return () => window.removeEventListener('storage-update', handleStorageUpdate);
  }, [setSessionContext]);

  // Initial load of sessionContext from localStorage on mount
  useEffect(() => {
    try {
      const sessionContextStr = localStorage.getItem('session_context');
      if (sessionContextStr) {
        const context = JSON.parse(sessionContextStr);
        setSessionContext(context);
      }
    } catch (error) {
      console.error('Failed to load initial sessionContext:', error);
    }
  }, [setSessionContext]);

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(() => {
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer
    autoSaveTimerRef.current = setTimeout(() => {
      console.log('[ChatArea] Triggering auto-save after message');

      // Auto-save unsaved sessions (creates "unsaved-" session)
      autoSaveSession();

      // Auto-save regular sessions if setting is enabled
      if (userSettings.autoSaveEnabled === true) {
        console.log('[ChatArea] Auto-save enabled, saving regular session');
        autoSaveRegularSession();
      }
    }, 2000); // Wait 2 seconds after message before auto-saving
  }, [autoSaveSession, autoSaveRegularSession, userSettings]);

  // A drawing an answer offered has been decided, or has finished being drawn. Written
  // back onto the message so it outlives this render — the thread is what gets saved, and
  // a drawing that vanished when the session was reopened would be worse than none.
  const handleImageProposal = useCallback((msgId: number, proposal: any) => {
    updateChatMessage(msgId, { image_proposal: proposal });
    debouncedAutoSave();
  }, [updateChatMessage, debouncedAutoSave]);

  // Stop waiting for an answer.
  //
  // It does not stop the worker — the answer is generated off a queue and will finish
  // whether anyone is listening — so this is about the page: forget the message, drop the
  // indicator, and put the text back in the box so it can be reworded and sent again.
  // Without a way to do that, a turn that never lands leaves a spinner the person cannot
  // clear, and refreshing the page only resumes it.
  const stopWaiting = useCallback((putTextBack: boolean) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    forgetPendingMessage();
    setIsGenerating(false);
    setIsLoading(false);
    setProcessingStatuses([]);
    setDrawingAsk(null);
    queuedStatusTimestampsRef.current.clear();

    if (putTextBack) {
      const messages = useSessionStore.getState().chatMessages;
      const lastIndex = messages.length - 1;
      if (lastIndex >= 0 && messages[lastIndex]?.role === 'user') {
        setMessage(String(messages[lastIndex].content || ''));
        deleteChatMessage(lastIndex);
      }
    }
  }, [deleteChatMessage]);

  // Draw it, answer in words instead, or drop the message.
  //
  // The first two resume the turn that asked — the worker kept the payload it arrived on,
  // so the same message carries on with the decision added and the poll already running
  // picks the answer up. Returning a string reports a problem back to the card.
  const handleDrawingDecision = useCallback(async (
    decision: 'generate' | 'answer' | 'cancel'
  ): Promise<string | void> => {
    const asking = drawingAsk;
    if (!asking) return;

    try {
      const result = await chatApi.decideDrawing(asking.messageId, decision);
      if (result?.error) return result.error;

      if (decision === 'cancel') {
        // Dropped, and the text goes back in the box. The quickest fix for a request that
        // was read as a drawing when it was not is to reword it, which needs the words.
        stopWaiting(true);
        return;
      }

      setDrawingAsk(null);
    } catch (error) {
      console.error('Failed to answer the drawing question:', error);
      return 'That did not go through. Try again.';
    }
  }, [drawingAsk, stopWaiting]);

  // Format messages for display - memoized to prevent re-renders
  const messages = useMemo(() => chatMessages.map((msg, index) => ({
    id: index,
    type: msg.role === 'user' ? 'user' : msg.role === 'system' ? 'system' : 'assistant',
    content: msg.content,
    timestamp: msg.receiveTime ? new Date(msg.receiveTime).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }) : new Date().toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }),
    message_context: msg.message_context,
    session_context: msg.session_context,
    documents: (msg as any).documents,
    token_usage: (msg as any).token_usage,
    image_s3_key: (msg as any).image_s3_key,
    image_proposal: (msg as any).image_proposal,
    userMessageId: (msg as any).userMessageId, // Preserve userMessageId for status history lookup
  })), [chatMessages]);

  // Define context-aware quick actions organized by category
  const quickActions: Array<{
    category: string;
    icon: any;
    label: string;
    color: string;
    prompt: string;
  }> = [];

  // Add document-specific quick actions if documents are present
  const hasPatents = sessionDocuments.some(doc => doc.file_type === 'patent' || doc.file_type === 'publication' || doc.file_type === 'foreign_patent');
  const hasApplications = sessionDocuments.some(doc => doc.file_type === 'application') || sessionApplications.length > 0;
  const hasUploads = sessionDocuments.some(doc => doc.file_type === 'upload');
  const hasMultipleDocs = sessionDocuments.length > 1;

  // PATENT OVERVIEW & SUMMARY
  if (hasPatents) {
    quickActions.push(
      { category: 'Overview & Summary', icon: BookOpen, label: 'Comprehensive Summary', color: 'text-blue-600', prompt: 'Provide a comprehensive summary of the selected documents, including the key innovations, claims scope, technical details, and practical applications.' },
      { category: 'Overview & Summary', icon: Sparkles, label: 'Executive Summary', color: 'text-blue-500', prompt: 'Create a brief executive summary of the selected documents suitable for non-technical stakeholders, highlighting the business value and key differentiators.' },
      { category: 'Overview & Summary', icon: FileText, label: 'Abstract Summary', color: 'text-blue-700', prompt: 'Summarize the abstract and main concepts of the selected documents in simple terms.' }
    );
  }

  // CLAIMS ANALYSIS
  if (hasPatents) {
    quickActions.push(
      { category: 'Claims Analysis', icon: Scale, label: 'Detailed Claims Analysis', color: 'text-purple-600', prompt: 'Analyze all claims in the selected documents in detail, including claim scope, dependencies, limitations, and claim structure (independent vs dependent).' },
      { category: 'Claims Analysis', icon: Scale, label: 'Independent Claims', color: 'text-purple-500', prompt: 'Analyze only the independent claims in the selected documents and explain their scope and key limitations.' },
      { category: 'Claims Analysis', icon: Scale, label: 'Claim Dependencies', color: 'text-purple-700', prompt: 'Map out the claim dependency tree for the selected documents and explain how dependent claims narrow the scope of independent claims.' },
      { category: 'Claims Analysis', icon: FileCheck, label: 'Claim Elements', color: 'text-purple-800', prompt: 'Break down each claim in the selected documents into its individual elements and explain what each element covers.' },
      { category: 'Claims Analysis', icon: BookOpen, label: 'Claims to Spec Support', color: 'text-cyan-600', prompt: 'Analyze how each claim in the selected documents is supported by the specification and identify any potential written description or enablement issues under 35 USC 112.' },
      { category: 'Claims Analysis', icon: Scale, label: 'Broadest Claim', color: 'text-purple-400', prompt: 'Identify the broadest claim in the selected documents and explain its scope and potential coverage.' }
    );
  }

  // SPECIFICATION ANALYSIS
  if (hasPatents) {
    quickActions.push(
      { category: 'Specification Analysis', icon: FileText, label: 'Technical Background', color: 'text-indigo-600', prompt: 'Summarize the technical background section of the selected documents, including the problems being solved and limitations discussed.' },
      { category: 'Specification Analysis', icon: FileCheck, label: 'Technical Solution', color: 'text-emerald-600', prompt: 'Explain the technical solutions disclosed in the selected documents and how the inventions work in detail.' },
      { category: 'Specification Analysis', icon: BookOpen, label: 'Embodiments Overview', color: 'text-indigo-500', prompt: 'Summarize all embodiments described in the selected documents, including preferred and alternative embodiments.' },
      { category: 'Specification Analysis', icon: FileText, label: 'Detailed Description', color: 'text-indigo-700', prompt: 'Provide a detailed explanation of the detailed description sections in the selected documents, including all technical details and variations.' },
      { category: 'Specification Analysis', icon: Sparkles, label: 'Key Features', color: 'text-green-600', prompt: 'What are the key technical features and innovations described in the selected documents?' },
      { category: 'Specification Analysis', icon: FileCheck, label: 'Enablement Check', color: 'text-amber-600', prompt: 'Review the specifications in the selected documents for enablement compliance under 35 USC 112(a) - does it enable a person skilled in the art to make and use the invention?' },
      { category: 'Specification Analysis', icon: BookOpen, label: 'Written Description Check', color: 'text-amber-700', prompt: 'Evaluate whether the specifications in the selected documents provide adequate written description support for all claims under 35 USC 112(a).' }
    );
  }

  // PRIOR ART & PATENTABILITY
  quickActions.push(
    { category: 'Prior Art & Patentability', icon: Sparkles, label: 'Patentability Assessment', color: 'text-emerald-700', prompt: 'Assess the overall patentability of the selected documents under 35 USC 101, 102, 103, and 112. Identify potential issues.' },
    { category: 'Prior Art & Patentability', icon: Scale, label: 'Compare Documents', color: 'text-violet-600', prompt: 'Compare the selected documents and explain how they relate to each other, identifying similarities and differences.' },
    { category: 'Prior Art & Patentability', icon: BookOpen, label: 'Novelty Analysis', color: 'text-emerald-600', prompt: 'Analyze the novelty of the selected documents under 35 USC 102 and identify what is new.' },
    { category: 'Prior Art & Patentability', icon: Sparkles, label: 'Non-Obviousness Analysis', color: 'text-emerald-500', prompt: 'Evaluate non-obviousness of the selected documents under 35 USC 103. Would it have been obvious to a person of ordinary skill in the art?' },
    { category: 'Prior Art & Patentability', icon: FileText, label: 'Search Strategy', color: 'text-violet-700', prompt: 'Based on the selected documents, suggest a search strategy including search terms, classifications, and key areas to investigate.' },
    { category: 'Prior Art & Patentability', icon: Scale, label: 'Distinguishing Features', color: 'text-green-700', prompt: 'What are the key distinguishing features between the selected documents?' }
  );

  // OFFICE ACTION RESPONSE - Applications only
  if (hasApplications) {
    quickActions.push(
      { category: 'Office Action Response', icon: Scale, label: 'OA Analysis', color: 'text-red-600', prompt: 'Analyze the selected office actions in detail, including all rejections, objections, and requirements. Provide a comprehensive response strategy.' },
      { category: 'Office Action Response', icon: FileCheck, label: 'Rejection Analysis', color: 'text-red-700', prompt: 'Analyze each rejection in the selected documents and explain the examiner\'s reasoning and references being applied.' },
      { category: 'Office Action Response', icon: BookOpen, label: 'Response Strategy', color: 'text-red-500', prompt: 'Provide a detailed response strategy for the selected office actions, including recommended arguments and potential amendments.' },
      { category: 'Office Action Response', icon: FileText, label: 'Amendment Suggestions', color: 'text-orange-600', prompt: 'Suggest specific claim amendments to overcome the rejections in the selected office actions while maintaining broad coverage.' },
      { category: 'Office Action Response', icon: Scale, label: 'Argument Points', color: 'text-red-800', prompt: 'Provide specific argument points to traverse the rejections in the selected documents, including relevant case law and MPEP sections.' },
      { category: 'Office Action Response', icon: Sparkles, label: 'Examiner Interview Prep', color: 'text-red-400', prompt: 'Prepare talking points and strategy for an examiner interview based on the selected documents.' }
    );
  }

  // APPLICATION STATUS - Applications only
  if (hasApplications) {
    quickActions.push(
      { category: 'Application Status', icon: FileCheck, label: 'Status Summary', color: 'text-indigo-700', prompt: 'What is the current status of the selected applications? Summarize the prosecution history and next steps.' },
      { category: 'Application Status', icon: BookOpen, label: 'Prosecution History', color: 'text-indigo-600', prompt: 'Provide a detailed timeline of the prosecution history for the selected applications, including all office actions and responses.' },
      { category: 'Application Status', icon: Sparkles, label: 'Next Steps', color: 'text-indigo-500', prompt: 'What are the recommended next steps for the selected applications based on their current status?' }
    );
  }

  // DOCUMENT COMPARISON - All types
  if (hasMultipleDocs || hasPatents) {
    quickActions.push(
      { category: 'Document Comparison', icon: Scale, label: 'Compare Claims', color: 'text-purple-700', prompt: 'Compare the claims across the selected documents. Identify similarities, differences, and relative claim scope.' },
      { category: 'Document Comparison', icon: FileText, label: 'Compare Inventions', color: 'text-blue-700', prompt: 'Compare the key innovations and technical solutions across the selected documents.' },
      { category: 'Document Comparison', icon: BookOpen, label: 'Compare Specifications', color: 'text-indigo-800', prompt: 'Compare the specifications in the selected documents, identifying common technical approaches and unique features.' },
      { category: 'Document Comparison', icon: Sparkles, label: 'Coverage Analysis', color: 'text-cyan-700', prompt: 'Analyze the overall coverage across the selected documents. Are there any gaps or overlaps?' },
      { category: 'Document Comparison', icon: FileCheck, label: 'Portfolio Strategy', color: 'text-emerald-800', prompt: 'Analyze the selected documents as a portfolio. How do they work together?' }
    );
  }

  // PATENT DRAFTING - Patents, publications, applications
  if (hasPatents || hasApplications) {
    quickActions.push(
      { category: 'Drafting Assistance', icon: FileText, label: 'Claim Drafting Tips', color: 'text-teal-600', prompt: 'Provide suggestions for improving the claim language and structure in the selected documents to maximize scope while maintaining clarity.' },
      { category: 'Drafting Assistance', icon: BookOpen, label: 'Spec Improvements', color: 'text-teal-700', prompt: 'Suggest improvements to the specifications in the selected documents to better support the claims and provide clear disclosure.' },
      { category: 'Drafting Assistance', icon: Sparkles, label: 'Alternative Embodiments', color: 'text-teal-500', prompt: 'Suggest additional embodiments or variations that should be included in the specifications of the selected documents.' },
      { category: 'Drafting Assistance', icon: FileCheck, label: 'Dependent Claim Ideas', color: 'text-teal-800', prompt: 'Suggest additional dependent claims for the selected documents that could provide valuable fallback positions.' }
    );
  }

  // TECHNICAL ANALYSIS - Patents, publications, applications, uploads
  if (hasPatents) {
    quickActions.push(
      { category: 'Technical Analysis', icon: FileText, label: 'Technical Field', color: 'text-slate-600', prompt: 'What is the technical field of the selected documents and what industry applications do they have?' },
      { category: 'Technical Analysis', icon: Sparkles, label: 'Advantages Analysis', color: 'text-slate-700', prompt: 'What are the technical advantages and benefits described in the selected documents?' },
      { category: 'Technical Analysis', icon: BookOpen, label: 'Implementation Details', color: 'text-slate-500', prompt: 'Explain the implementation details and technical requirements described in the selected documents.' },
      { category: 'Technical Analysis', icon: FileCheck, label: 'Limitations Analysis', color: 'text-slate-800', prompt: 'What are the technical limitations and potential challenges described in the selected documents?' }
    );
  }

  // LEGAL GUIDANCE - All types
  quickActions.push(
    { category: 'Legal Guidance', icon: BookOpen, label: 'Relevant MPEP Sections', color: 'text-cyan-700', prompt: 'What MPEP sections are most relevant to the selected documents? Provide specific section numbers and brief summaries.' },
    { category: 'Legal Guidance', icon: Scale, label: 'Relevant Case Law', color: 'text-cyan-600', prompt: 'What case law is relevant to the legal issues in the selected documents?' },
    { category: 'Legal Guidance', icon: FileText, label: 'CFR Requirements', color: 'text-cyan-800', prompt: 'What 37 CFR requirements apply to the selected documents or matter?' },
    { category: 'Legal Guidance', icon: BookOpen, label: 'USC Analysis', color: 'text-cyan-500', prompt: 'Analyze the 35 USC requirements (101, 102, 103, 112) as they apply to the selected documents.' }
  );

  // UPLOADS / GENERAL - Uploads only
  if (hasUploads) {
    quickActions.push(
      { category: 'Document Analysis', icon: FileText, label: 'Extract Key Info', color: 'text-amber-700', prompt: 'Extract and summarize the key information from the selected documents.' },
      { category: 'Document Analysis', icon: BookOpen, label: 'Document Summary', color: 'text-amber-600', prompt: 'Provide a comprehensive summary of the selected documents.' },
      { category: 'Document Analysis', icon: Sparkles, label: 'Action Items', color: 'text-amber-500', prompt: 'Identify any action items, deadlines, or requirements mentioned in the selected documents.' }
    );
  }

  // Group actions by category for display
  const groupedActions = quickActions.reduce((acc, action) => {
    if (!acc[action.category]) {
      acc[action.category] = [];
    }
    acc[action.category].push(action);
    return acc;
  }, {} as Record<string, typeof quickActions>);

  // Handle quick action clicks - fill chat input with prompt
  const handleQuickAction = (action: typeof quickActions[0]) => {
    setMessage(action.prompt);
    // Focus on textarea after setting message
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 50);
  };

  const updateSessionContext = (context: any) => {
    if (
      context &&
      context !== 'null' &&
      typeof context === 'object' &&
      Object.keys(context).length > 0
    ) {
      localStorage.setItem('session_context', JSON.stringify(context));
      window.dispatchEvent(new Event('storage-update'));
    }
  };

  // Get active documents from session context
  const getActiveDocuments = () => {
    try {
      const sessionContextStr = localStorage.getItem('session_context');
      if (!sessionContextStr) return [];

      const context = JSON.parse(sessionContextStr);
      const documents: Array<{ id: string; title: string; type: string }> = [];

      // Patents
      (context.current_patents || []).forEach((id: string) => {
        documents.push({ id, title: `Patent ${id}`, type: 'Patent' });
      });

      // Applications
      (context.current_applications || []).forEach((id: string) => {
        documents.push({ id, title: `Application ${id}`, type: 'Application' });
      });

      // Publications
      (context.current_publications || []).forEach((id: string) => {
        documents.push({ id, title: `Publication ${id}`, type: 'Publication' });
      });

      // Foreign Patents
      (context.current_foreign_patents || []).forEach((id: string) => {
        documents.push({ id, title: id, type: 'Foreign Patent' });
      });

      // Uploads
      (context.resource_names || []).forEach((name: string) => {
        documents.push({ id: name, title: name, type: 'Upload' });
      });

      // MPEP Sections
      (context.mpep_sections || []).forEach((section: string) => {
        documents.push({ id: section, title: `MPEP ${section}`, type: 'MPEP' });
      });

      // CFR Sections
      (context.cfr_sections || []).forEach((section: string) => {
        documents.push({ id: section, title: `37 CFR ${section}`, type: 'CFR' });
      });

      // USC Sections
      (context.usc_sections || []).forEach((section: string) => {
        documents.push({ id: section, title: `35 USC ${section}`, type: 'USC' });
      });

      // Drafts - add from activeDraftsInChat state
      activeDraftsInChat.forEach((draft: any) => {
        documents.push({
          id: draft.draft_id,
          title: draft.title || 'Untitled Draft',
          type: 'Draft'
        });
      });

      // Prosecution Documents - add from localStorage session_context
      const prosDocIds = context.pros_docs || [];
      const prosDocNames = context.pros_doc_names || [];

      prosDocIds.forEach((id: string, index: number) => {
        documents.push({
          id,
          title: prosDocNames[index] || id,
          type: 'Prosecution Document'
        });
      });

      return documents;
    } catch (error) {
      console.error('Failed to get session documents:', error);
      return [];
    }
  };

  // Add draft as active document
  const addDraftAsActiveDocument = () => {
    if (!activeDraft) return;

    // Check if draft is already in active documents
    const isAlreadyActive = activeDraftsInChat.some(d => d.draft_id === activeDraft.draft_id);
    if (isAlreadyActive) {
      addNotification({
        type: 'info',
        title: 'Already Active',
        message: 'This draft is already in Session documents',
        duration: 2000
      });
      return;
    }

    // Add to active drafts
    setActiveDraftsInChat(prev => [...prev, {
      draft_id: activeDraft.draft_id,
      title: draftTitle || activeDraft.title,
      content: draftContent
    }]);

    addNotification({
      type: 'success',
      title: 'Added to session',
      message: `"${draftTitle || activeDraft.title}" can now be referenced with @`,
      duration: 3000
    });
  };

  // Remove draft from active documents
  const removeDraftFromActiveDocuments = (draftId: string) => {
    setActiveDraftsInChat(prev => prev.filter(d => d.draft_id !== draftId));
  };

  // Get all active documents (memoized to react to state changes)
  const allActiveDocuments = useMemo(() => {
    const docs = getActiveDocuments();
    console.log('[allActiveDocuments] All session documents:', docs);
    console.log('[allActiveDocuments] Foreign patents:', docs.filter(d => d.type === 'Foreign Patent'));
    return docs;
  }, [activeDraftsInChat, sessionContext]);

  // Filter documents based on mention filter
  const filteredDocuments = useMemo(() => {
    if (!mentionFilter) return allActiveDocuments;

    const filter = mentionFilter.toLowerCase();
    return allActiveDocuments.filter(doc =>
      doc.title.toLowerCase().includes(filter) ||
      doc.type.toLowerCase().includes(filter)
    );
  }, [mentionFilter, allActiveDocuments]);

  // Helper to parse task result content
  // Extract result data from various possible property names
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;

    // Check multiple possible property names where the actual data might be stored
    // Priority: result > output > analysis > use the object itself
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;

    // If none of the above exist, assume the object itself is the data
    return resultObj;
  };

  // Handlers for MPEP/CFR/USC loading
  const handleLoadMpepSection = async (section: string) => {
    if (!section.trim()) return;
    setLoadingMpepSection(true);
    try {
      const doc = await resourceApi.fetchMpepContent(section);
      // Add to chat documents
      onDocumentClick({
        ...doc,
        type: 'mpep',
        resource_id: section,
        file_name: `MPEP ${section}`
      });
    } catch (error: any) {
      addNotification({
        message: error.response?.data?.detail || 'Failed to load MPEP section',
        type: 'error'
      });
    } finally {
      setLoadingMpepSection(false);
    }
  };

  const handleLoadCfrSection = async (section: string) => {
    if (!section.trim()) return;
    setLoadingCfrSection(true);
    try {
      const doc = await resourceApi.fetchCfrContent(section);
      // Add to chat documents
      onDocumentClick({
        ...doc,
        type: 'cfr',
        resource_id: section,
        file_name: `37 CFR ${section}`
      });
    } catch (error: any) {
      addNotification({
        message: error.response?.data?.detail || 'Failed to load CFR section',
        type: 'error'
      });
    } finally {
      setLoadingCfrSection(false);
    }
  };

  const toggleMpepChapter = (chapterId: string) => {
    setExpandedMpepChapters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  const toggleCfrPart = (partKey: string) => {
    setExpandedCfrParts(prev => ({
      ...prev,
      [partKey]: !prev[partKey]
    }));
  };

  const toggleCfrSubpart = (subpartKey: string) => {
    setExpandedCfrSubparts(prev => ({
      ...prev,
      [subpartKey]: !prev[subpartKey]
    }));
  };

  // Load all documents functions
  const loadAllPatents = async () => {
    setLoadingPatents(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const patents = documents.filter((doc: any) => doc.file_type === 'patent');
      setAllPatentDocs(patents);
      setPatientsLoaded(true);
    } catch (error) {
      console.error('Failed to load all patents:', error);
      addNotification({
        message: 'Failed to load all patents',
        type: 'error'
      });
    } finally {
      setLoadingPatents(false);
    }
  };

  const loadAllPublications = async () => {
    setLoadingPublications(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const publications = documents.filter((doc: any) => doc.file_type === 'publication');
      setAllPublicationDocs(publications);
      setPublicationsLoaded(true);
    } catch (error) {
      console.error('Failed to load all publications:', error);
      addNotification({
        message: 'Failed to load all publications',
        type: 'error'
      });
    } finally {
      setLoadingPublications(false);
    }
  };

  const loadAllUploads = async () => {
    setLoadingUploads(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const uploads = documents.filter((doc: any) => doc.file_type === 'upload');
      setAllUploadDocs(uploads);
      setUploadsLoaded(true);
    } catch (error) {
      console.error('Failed to load all uploads:', error);
      addNotification({
        message: 'Failed to load all uploads',
        type: 'error'
      });
    } finally {
      setLoadingUploads(false);
    }
  };

  const loadAllForeignPatents = async () => {
    setLoadingForeignPatents(true);
    try {
      const documents = await documentApi.fetchSessionDocuments();
      const foreignPatents = documents.filter((doc: any) => doc.file_type === 'foreign_patent');
      setAllForeignPatentDocs(foreignPatents);
      setForeignPatientsLoaded(true);
    } catch (error) {
      console.error('Failed to load all foreign patents:', error);
      addNotification({
        message: 'Failed to load all foreign patents',
        type: 'error'
      });
    } finally {
      setLoadingForeignPatents(false);
    }
  };

  // Parse result output - it comes as a string that may need parsing
  const getParsedResult = (output: any) => {
    if (!output) return null;

    // If it's already an object, return it
    if (typeof output === 'object') return output;

    // If it's a string, try to parse it as JSON
    if (typeof output === 'string') {
      const trimmed = output.trim();

      // If it doesn't look like JSON/dict, return as-is
      if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
        return output;
      }

      try {
        // Try to parse as JSON first
        return JSON.parse(output);
      } catch {
        // If parsing fails and it contains single quotes, try Python dict conversion
        if (output.includes("'")) {
          try {
            // More robust Python to JSON conversion
            // Handle single quotes more carefully to avoid breaking contractions
            let jsonStr = output;

            // Replace single quotes only in structural positions (keys, values, arrays)
            jsonStr = jsonStr.replace(/'([^']*)':/g, '"$1":');  // Keys
            jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');  // Values after colons
            jsonStr = jsonStr.replace(/\[\s*'([^']*)'/g, '["$1"');  // Array first item
            jsonStr = jsonStr.replace(/,\s*'([^']*)'/g, ', "$1"');  // Array other items
            jsonStr = jsonStr.replace(/'([^']*)']/g, '"$1"]');  // Array last item

            // Handle Python boolean and null values
            jsonStr = jsonStr.replace(/True/g, 'true');
            jsonStr = jsonStr.replace(/False/g, 'false');
            jsonStr = jsonStr.replace(/None/g, 'null');

            return JSON.parse(jsonStr);
          } catch (e) {
            console.warn('Failed to parse Python dict:', output.substring(0, 100) + '...', 'Error:', e);
            // If all parsing fails, return the string as-is
            return output;
          }
        }

        // If JSON parse failed but no single quotes, return the string
        console.warn('Failed to parse JSON:', output.substring(0, 100) + '...');
        return output;
      }
    }

    return output;
  };

  // Render task result display component
  const renderTaskResultDisplay = (taskResult: TaskResultAttachment) => {
    // The content is already the extracted result data (stringified)
    // Just parse it directly - no need to call getResultData again
    const parsedData = getParsedResult(taskResult.content);

    console.log('=== ChatArea Task Result Debug ===');
    console.log('Task Type:', taskResult.taskType);
    console.log('Task Name:', taskResult.taskName);
    console.log('Raw Content (first 200 chars):', taskResult.content.substring(0, 200));
    console.log('Parsed Data:', parsedData);
    console.log('Parsed Data Type:', typeof parsedData);
    console.log('==================================');

    const commonProps = {
      result: parsedData,
      theme: 'light' as const,
      showHeader: true,
      showToast,
      onCopyResult: () => copyResult(parsedData, taskResult.taskType),
    };

    // Try to infer task type from task name or content
    const taskType = taskResult.taskType;

    switch (taskType) {
      case 'parts_list':
        return <PartsListDisplay {...commonProps} showToggle={true} />;
      case 'spec_map':
        return <SpecMapDisplay {...commonProps} showToggle={true} />;
      case 'claims_support':
        return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
      case 'claims_with_numerals':
        return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
      case 'custom_analysis':
        return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
      case 'ir_metadata':
        return <IRMetadataDisplay {...commonProps} />;
      case 'ir_innovation':
        return <IRInnovationAnalysisDisplay {...commonProps} />;
      case 'ir_tech_classification':
        return <IRTechClassificationDisplay {...commonProps} />;
      case 'draft_summary':
        return <DraftSummaryDisplay {...commonProps} />;
      case 'draft_detailed_description':
        return <DraftDetailedDescriptionDisplay {...commonProps} />;
      case 'draft_background':
        return <DraftBackgroundDisplay {...commonProps} />;
      case 'draft_claims':
        return <DraftClaimsDisplay {...commonProps} />;
      case 'draft_other_embodiments':
        return <OtherEmbodimentsDisplay {...commonProps} />;
      case 'oa_extract':
        return <OAExtractDisplay {...commonProps} />;
      case 'oa_analyze':
        return <OAAnalyzeDisplay {...commonProps} />;
      case 'oa_summary_art':
        return <OASummaryArtDisplay {...commonProps} />;
      case 'oa_timeline':
        return <OATimelineDisplay {...commonProps} />;
      case 'translate_document':
        return <TranslateDocumentDisplay {...commonProps} />;
      default:
        // Fallback to displaying raw JSON
        return (
          <div className="p-4 rounded-lg bg-gray-50">
            <pre className="text-xs overflow-auto whitespace-pre-wrap">
              {typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)}
            </pre>
          </div>
        );
    }
  };

  // Create stable reference for onDocumentClick to prevent markdownOverrides from recreating
  const onDocumentClickRef = useRef(onDocumentClick);
  useEffect(() => {
    onDocumentClickRef.current = onDocumentClick;
  }, [onDocumentClick]);

  const stableOnDocumentClick = useCallback((doc: any) => {
    onDocumentClickRef.current(doc);
  }, []);

  // Preprocess assistant messages: convert tags to HTML spans that Markdown will preserve
  const preprocessAssistantMessage = useCallback((text: string, messageId?: number) => {
    return preprocessAssistantMarkdown(text);
  }, []);

  // Parse message to find document tags and task tags (for user messages)
  const parseMessageTags = (text: string) => {
    // Pre-process: Remove bold markers (**) around tags to prevent rendering issues
    // Convert single newlines to spaces (Markdown behavior), but preserve paragraph breaks (double newlines)

    // First, normalize line endings (handle \r\n)
    text = text.replace(/\r\n/g, '\n');

    // Protect paragraph breaks by marking them (two or more consecutive newlines)
    text = text.replace(/\n\n+/g, '___PARAGRAPH_BREAK___');

    // Handle bold markers around tags - split bold when tag is mixed with other text
    // Pattern: **text[TAG]text** should become **text** [TAG] **text**

    // First, handle the complete pattern: **...[TAG]...**
    text = text.replace(/\*\*([^\*\[]*?)(\[(?:PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|USC|DRAFT|PROS_DOC|Task:|Task Result:)[^\]]+\])([^\*]*?)\*\*/g, (match, before, tag, after) => {
      const trimmedBefore = before.trim();
      const trimmedAfter = after.trim();

      // Build the result based on what text exists
      let result = '';
      if (trimmedBefore) {
        result += `**${trimmedBefore}** `;
      }
      result += tag;
      if (trimmedAfter) {
        result += ` **${trimmedAfter}**`;
      }
      return result;
    });

    // Handle remaining ** before tag (no closing **)
    text = text.replace(/\*\*([^\[]*?)(\[(?:PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|USC|DRAFT|PROS_DOC|Task:|Task Result:)[^\]]+\])/g, (match, before, tag) => {
      const trimmedBefore = before.trim();
      if (trimmedBefore) {
        return `**${trimmedBefore}** ${tag}`;
      }
      return tag;
    });

    // Handle remaining ** after tag (no opening **)
    text = text.replace(/(\[(?:PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|USC|DRAFT|PROS_DOC|Task:|Task Result:)[^\]]+\])([^\*]*?)\*\*/g, (match, tag, after) => {
      const trimmedAfter = after.trim();
      if (trimmedAfter) {
        return `${tag} **${trimmedAfter}**`;
      }
      return tag;
    });

    // Clean up any remaining ** directly adjacent to tags
    text = text.replace(/\*\*\s*(\[(?:PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|USC|DRAFT|PROS_DOC|Task:|Task Result:)[^\]]+\])/g, ' $1');
    text = text.replace(/(\[(?:PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|USC|DRAFT|PROS_DOC|Task:|Task Result:)[^\]]+\])\s*\*\*/g, '$1 ');

    // Now convert ALL single newlines and whitespace sequences around tags to single spaces
    // Match tag followed by any whitespace (including newlines) that doesn't contain paragraph break
    text = text.replace(/(\[(?:PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|USC|DRAFT|PROS_DOC|Task:|Task Result:)[^\]]+\])[ \t]*\n[ \t]*(?!___PARAGRAPH_BREAK___)/g, '$1 ');
    // Match newline and whitespace before a tag
    text = text.replace(/\n[ \t]*(\[(?:PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|UFC|DRAFT|PROS_DOC|Task:|Task Result:)[^\]]+\])/g, ' $1');

    // Restore paragraph breaks
    text = text.replace(/___PARAGRAPH_BREAK___/g, '\n\n');

    const docTagRegex = /\[(PATENT|PUBLICATION|APPLICATION|FOREIGN_PATENT|UPLOAD|MPEP|CFR|USC|DRAFT|PROS_DOC)_?([^\]]+)\]/g;
    const taskTagRegex = /\[Task:\s*([^#\]]+)(?:#([^\]]+))?\]/g;
    const taskResultTagRegex = /\[Task Result:\s*([^#]+)#([^#]+)#([^|\]]+)(?:\s*\|\s*Document ID:\s*([^\]]+))?\]/g;
    const parts: Array<{ type: 'text' | 'tag' | 'taskTag'; content: string; docType?: string; docId?: string; taskName?: string; taskId?: string; taskType?: string; taskContent?: string }> = [];

    // Find all matches (document tags, task tags, and task result tags)
    const allMatches: Array<{ index: number; length: number; type: 'doc' | 'task'; data: any }> = [];

    let match;
    while ((match = docTagRegex.exec(text)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        type: 'doc',
        data: { docType: match[1], docId: match[2], content: match[0] }
      });
    }

    while ((match = taskTagRegex.exec(text)) !== null) {
      allMatches.push({
        index: match.index,
        length: match[0].length,
        type: 'task',
        data: { taskName: match[1].trim(), taskId: match[2], content: match[0] }
      });
    }

    // Reset regex for finding task results with their content
    taskResultTagRegex.lastIndex = 0;
    while ((match = taskResultTagRegex.exec(text)) !== null) {
      const headerEndIndex = match.index + match[0].length;

      // Find the next task result tag or end of text
      const remainingText = text.substring(headerEndIndex);
      const nextTagMatch = /\[(?:Task Result:|PATENT|PUBLICATION|APPLICATION|UPLOAD|MPEP|CFR|USC|PROS_DOC|Task:)/g.exec(remainingText);
      const contentEndIndex = nextTagMatch ? headerEndIndex + nextTagMatch.index : text.length;

      // Extract the content between this tag and the next tag (or end)
      // This includes any leading whitespace/newlines after the header
      const fullContent = text.substring(headerEndIndex, contentEndIndex);
      const taskContent = fullContent.trim();

      const taskData = {
        taskName: match[1].trim(),
        taskId: match[2].trim(),
        taskType: match[3].trim(),
        docId: match[4] ? match[4].trim() : undefined,
        content: match[0],
        taskContent: taskContent
      };

      allMatches.push({
        index: match.index,
        // Include the full length: header + whitespace + content
        length: match[0].length + fullContent.length,
        type: 'task',
        data: taskData
      });
    }

    // Sort matches by index
    allMatches.sort((a, b) => a.index - b.index);

    let lastIndex = 0;
    for (const match of allMatches) {
      // Add text before the tag
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
      }

      // Add the tag
      if (match.type === 'doc') {
        parts.push({
          type: 'tag',
          content: match.data.content,
          docType: match.data.docType,
          docId: match.data.docId
        });
      } else {
        parts.push({
          type: 'taskTag',
          content: match.data.content,
          taskName: match.data.taskName,
          taskId: match.data.taskId,
          taskType: match.data.taskType,
          taskContent: match.data.taskContent,
          docId: match.data.docId
        });
      }

      lastIndex = match.index + match.length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex) });
    }

    return parts;
  };

  // Handle clicking on a document tag
  const handleTagClick = useCallback((docType: string, docId: string) => {
    // Handle drafts specially - open in chat split view
    if (docType === 'DRAFT') {
      console.log('[ChatArea] Opening draft from tag click:', { docType, docId });

      // If docId looks like a title (contains spaces or is not a UUID), try to find the draft_id
      const draft = activeDraftsInChat.find(d =>
        d.draft_id === docId || d.title === docId
      );

      if (draft) {
        console.log('[ChatArea] Found draft:', draft);
        localStorage.setItem('open_draft_id', draft.draft_id);
        window.dispatchEvent(new Event('open-draft'));
      } else {
        console.error('[ChatArea] Draft not found in activeDraftsInChat:', docId);
        addNotification({
          type: 'error',
          title: 'Draft Not Found',
          message: 'The draft could not be found. It may have been deleted.',
          duration: 3000
        });
      }
      return;
    }

    // Open other document types in the viewer
    const titleCaseType = toTitleCaseDocType(docType);
    onDocumentClick({
      resource_id: docId,
      type: titleCaseType,
      title: `${titleCaseType} ${docId}`
    });
  }, [activeDraftsInChat, addNotification, onDocumentClick]);

  // Get icon for document type
  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'Patent':
      case 'Publication':
      case 'Application':
      case 'Foreign Patent':
        return FileText;
      case 'Upload':
        return FileCheck;
      case 'MPEP':
        return BookOpen;
      case 'CFR':
        return Scale;
      case 'Draft':
        return FileEdit;
      case 'Prosecution Document':
      case 'PROS_DOC':
        return FileText;
      default:
        return FileText;
    }
  };

  // Sync overlay scroll with textarea scroll
  const handleTextareaScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (textareaRef.current) {
      setScrollTop(textareaRef.current.scrollTop);
    }
  };

  // Handle message input change and detect @ mentions
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setMessage(value);

    // Check for @ mention trigger
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      const isValidMention = charBeforeAt === ' ' || charBeforeAt === '\n';

      if (isValidMention) {
        const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
        // Check if there's no whitespace after @
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          setMentionFilter(textAfterAt);
          setMentionStartPos(lastAtIndex);
          setShowMentionDropdown(true);
          setSelectedMentionIndex(0);
          return;
        }
      }
    }

    // Hide dropdown if no valid @ mention
    setShowMentionDropdown(false);
  };

  // Handle selecting a mention
  const handleMentionSelect = (doc: { id: string; title: string; type: string }) => {
    if (!textareaRef.current) return;

    console.log('[handleMentionSelect] Selected document:', doc);

    const beforeMention = message.substring(0, mentionStartPos);
    const afterMention = message.substring(textareaRef.current.selectionStart);

    // Format the mention based on document type
    let mentionText = '';
    if (doc.type === 'Patent') {
      mentionText = `[PATENT_${doc.id}]`;
    } else if (doc.type === 'Publication') {
      mentionText = `[PUBLICATION_${doc.id}]`;
    } else if (doc.type === 'Application') {
      mentionText = `[APPLICATION_${doc.id}]`;
    } else if (doc.type === 'Foreign Patent') {
      mentionText = `[FOREIGN_PATENT_${doc.id}]`;
      console.log('[handleMentionSelect] Created foreign patent tag:', mentionText);
    } else if (doc.type === 'Upload') {
      mentionText = `[UPLOAD_${doc.id}]`;
    } else if (doc.type === 'MPEP') {
      mentionText = `[MPEP_${doc.id}]`;
    } else if (doc.type === 'CFR') {
      mentionText = `[CFR_${doc.id}]`;
    } else if (doc.type === 'USC') {
      mentionText = `[USC_${doc.id}]`;
    } else if (doc.type === 'Draft') {
      // Format: [DRAFT_title] - simple and clean, ID looked up from activeDraftsInChat
      mentionText = `[DRAFT_${doc.title}]`;
    } else if (doc.type === 'Prosecution Document') {
      mentionText = `[PROS_DOC_${doc.id}]`;
    } else {
      mentionText = `@${doc.title}`;
    }

    const newMessage = `${beforeMention}${mentionText} ${afterMention}`;

    setMessage(newMessage);
    setShowMentionDropdown(false);
    setMentionFilter('');

    // Set cursor position after the mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStartPos + mentionText.length + 1;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Handle keyboard navigation in mention dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionDropdown) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      return;
    }

    // Handle mention dropdown navigation
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev =>
        prev < filteredDocuments.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (filteredDocuments.length > 0) {
        handleMentionSelect(filteredDocuments[selectedMentionIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowMentionDropdown(false);
    }
  };

  // Handle copying message to clipboard
  const handleCopyMessage = useCallback(async (content: string, messageId?: number) => {
    try {
      // Strip <Rsrc> tags and replace with visible numbers only
      const cleanContent = stripResourceTags(content);
      await navigator.clipboard.writeText(cleanContent);

      // Show "Copied" indicator for this message
      if (messageId !== undefined) {
        setCopiedMessageId(messageId);
        // Reset after 2 seconds
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  }, [stripResourceTags]);

  // Handle initiating message deletion
  const handleDeleteMessage = useCallback((messageId: number) => {
    setMessageToDelete(messageId);
    setShowDeleteDialog(true);
  }, []);

  // Handle confirming message deletion
  const confirmDeleteMessage = () => {
    if (messageToDelete !== null) {
      deleteChatMessage(messageToDelete);
      setMessageToDelete(null);
    }
    setShowDeleteDialog(false);
  };

  // Handle canceling message deletion
  const cancelDeleteMessage = () => {
    setMessageToDelete(null);
    setShowDeleteDialog(false);
  };

  // Handle creating a quick prompt from a message
  const handleCreatePromptFromMessage = (messageContent: string) => {
    // Set editing prompt with the message content, but no id (indicating new prompt)
    setEditingPrompt({ id: '', label: '', prompt: messageContent });
    setShowCustomPromptModal(true);
  };

  // Handle sending positive feedback
  const handleSendPositiveFeedback = useCallback(async (messageText: string, messageId: number) => {
    try {
      await chatApi.sendFeedback(messageText, true, sessionId);
      setMessageFeedback(prev => ({ ...prev, [messageId]: 'positive' }));
      showFeedbackToast(messageId, 'Feedback sent!');
    } catch (error) {
      console.error('Failed to send positive feedback:', error);
      showFeedbackToast(messageId, 'Failed to send feedback');
    }
  }, [sessionId]);

  // Handle sending negative feedback
  const handleSendNegativeFeedback = useCallback(async (messageText: string, messageId: number) => {
    try {
      await chatApi.sendFeedback(messageText, false, sessionId);
      setMessageFeedback(prev => ({ ...prev, [messageId]: 'negative' }));
      showFeedbackToast(messageId, 'Feedback sent!');
    } catch (error) {
      console.error('Failed to send negative feedback:', error);
      showFeedbackToast(messageId, 'Failed to send feedback');
    }
  }, [sessionId]);

  // Show feedback toast message
  const showFeedbackToast = useCallback((messageId: number, message: string, duration = 3000) => {
    setFeedbackToasts(prev => ({ ...prev, [messageId]: message }));
    setTimeout(() => {
      setFeedbackToasts(prev => {
        const newToasts = { ...prev };
        delete newToasts[messageId];
        return newToasts;
      });
    }, duration);
  }, []);

  // Helper function to check if user is near bottom and scroll
  const scrollToBottomIfNearBottom = useCallback(() => {
    if (!messagesEndRef.current) return;

    // Find the ScrollArea viewport (the actual scrollable element)
    const viewport = messagesEndRef.current.closest('[data-radix-scroll-area-viewport]') as HTMLElement;

    if (viewport) {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      const threshold = 200; // pixels from bottom to trigger auto-scroll

      // Only auto-scroll if user is near the bottom
      if (distanceFromBottom < threshold) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Fallback: just scroll if viewport not found
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Auto-scroll to bottom when messages change or status updates arrive
  useEffect(() => {
    scrollToBottomIfNearBottom();
  }, [chatMessages, isLoading, isGenerating, processingStatuses, scrollToBottomIfNearBottom]);

  // Reset selected mention index when filtered documents change
  useEffect(() => {
    if (selectedMentionIndex >= filteredDocuments.length) {
      setSelectedMentionIndex(Math.max(0, filteredDocuments.length - 1));
    }
  }, [filteredDocuments, selectedMentionIndex]);

  // Manage delay message timer
  useEffect(() => {
    if (isLoading || isGenerating) {
      // Start timer when loading or generating begins
      setShowDelayMessage(false);
      delayMessageTimerRef.current = setTimeout(() => {
        setShowDelayMessage(true);
      }, 60000); // 1 minute
    } else {
      // Clear timer and hide message when loading/generating stops
      if (delayMessageTimerRef.current) {
        clearTimeout(delayMessageTimerRef.current);
        delayMessageTimerRef.current = null;
      }
      setShowDelayMessage(false);
    }

    // Cleanup timer on component unmount
    return () => {
      if (delayMessageTimerRef.current) {
        clearTimeout(delayMessageTimerRef.current);
        delayMessageTimerRef.current = null;
      }
    };
  }, [isLoading, isGenerating]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Scroll to bottom on mount so returning to chat shows latest messages
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
    }, 50);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume polling if there was an in-progress message when we navigated away
  useEffect(() => {
    const pendingId = localStorage.getItem(PENDING_MSG_KEY);
    if (!pendingId || pollingIntervalRef.current) return;
    setIsGenerating(true);
    const resumedStatusTimestamps = new Set<number>();
    const tick = async () => {
      try {
        const messageResponse = await chatApi.getMessage(pendingId);
        // Restore any status updates already collected before we navigated away
        if (messageResponse.processing_status_history && Array.isArray(messageResponse.processing_status_history)) {
          const newStatuses = messageResponse.processing_status_history.filter(
            (s: any) => !resumedStatusTimestamps.has(s.timestamp)
          );
          if (newStatuses.length > 0) {
            newStatuses.forEach((s: any) => resumedStatusTimestamps.add(s.timestamp));
            setProcessingStatuses(messageResponse.processing_status_history.map((s: any) => ({
              status: s.status,
              message: s.details?.message || '',
              timestamp: s.timestamp,
              documents: s.details?.documents,
              resources: s.details?.resources,
              classification: s.details?.classification,
            })));
          }
        }
        // The turn stopped to ask whether to draw something. Nothing is coming until it
        // is answered, so the question goes up and the poll keeps running.
        if (messageResponse.image_proposal?.status === 'asked') {
          setDrawingAsk({ messageId: pendingId, drawing: messageResponse.image_proposal });
          setProcessingStatuses([]);
          return;
        }
        if (messageResponse.message &&
            messageResponse.message !== '' &&
            messageResponse.message !== 'null' &&
            messageResponse.message !== null) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          localStorage.removeItem(PENDING_MSG_KEY);
          setIsGenerating(false);
          setProcessingStatuses([]);
          addChatMessage({
            role: 'assistant' as const,
            content: messageResponse.message,
            receiveTime: Date.now(),
            userMessageId: pendingId,
            message_context: messageResponse.message_context,
            session_context: messageResponse.session_context,
            image_proposal: messageResponse.image_proposal,
          });
          updateSessionContext(messageResponse.session_context);
        }
      } catch (error) {
        console.error('Error resuming poll for message:', error);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        localStorage.removeItem(PENDING_MSG_KEY);
        setIsGenerating(false);
      }
    };
    // Straight away, not in three and a half seconds. A page reopened on a turn that is
    // waiting on the ATTORNEY — "shall I draw this?" — should put the question back up at
    // once, rather than showing a thinking indicator for a turn that is not thinking.
    void tick();
    pollingIntervalRef.current = setInterval(tick, 3500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for external requests to open document panel
  useEffect(() => {
    const handleOpenDocumentPanel = () => {
      setShowDocumentPanel(true);
    };

    window.addEventListener('open-document-panel', handleOpenDocumentPanel);
    return () => window.removeEventListener('open-document-panel', handleOpenDocumentPanel);
  }, []);

  // Load MPEP and CFR structures when document panel is opened
  useEffect(() => {
    if (showDocumentPanel) {
      // Load MPEP structure
      if (!mpepStructure && !loadingMpepStructure) {
        setLoadingMpepStructure(true);
        resourceApi.fetchMpepInfo()
          .then(structure => setMpepStructure(structure))
          .catch(() => {})
          .finally(() => setLoadingMpepStructure(false));
      }

      // Load CFR structure from static JSON file
      if (!cfrStructure && !loadingCfrStructure) {
        setLoadingCfrStructure(true);
        fetch('/cfr_info.json')
          .then(response => response.json())
          .then(data => setCfrStructure(data))
          .catch(() => {})
          .finally(() => setLoadingCfrStructure(false));
      }
    }
  }, [showDocumentPanel]);

  // Load drafts when draft panel is opened
  useEffect(() => {
    if (showDraftPanel && !loadingDrafts) {
      setLoadingDrafts(true);
      draftApi.listDrafts()
        .then(response => {
          setDrafts(response.drafts || []);
        })
        .catch(error => {
          console.error('Failed to load drafts:', error);
        })
        .finally(() => setLoadingDrafts(false));
    }
  }, [showDraftPanel]);

  // Listen for draft updates from other components
  useEffect(() => {
    const handleDraftsUpdated = () => {
      // Reload drafts if they've been loaded before
      if (drafts.length > 0 || showDraftPanel) {
        setLoadingDrafts(true);
        draftApi.listDrafts()
          .then(response => {
            setDrafts(response.drafts || []);
          })
          .catch(error => {
            console.error('Failed to reload drafts:', error);
          })
          .finally(() => setLoadingDrafts(false));
      }
    };

    window.addEventListener('drafts-updated', handleDraftsUpdated);
    return () => window.removeEventListener('drafts-updated', handleDraftsUpdated);
  }, [drafts.length, showDraftPanel]);

  // Save active drafts to localStorage when they change
  useEffect(() => {
    localStorage.setItem('active_drafts', JSON.stringify(activeDraftsInChat));
    // Don't dispatch 'storage-update' here - it would create an infinite loop
    // with the listener above that updates activeDraftsInChat on storage-update
  }, [activeDraftsInChat]);

  // Listen for draft open events from sidebar and MessageContext
  useEffect(() => {
    const handleOpenDraft = async () => {
      const draftId = localStorage.getItem('open_draft_id');
      if (!draftId) return;

      // Check if user has premium access
      if (!isPremium) {
        localStorage.removeItem('open_draft_id');
        addNotification({
          type: 'warning',
          title: 'Premium Feature',
          message: 'Drafts is a premium-only feature. Please upgrade your account to access this functionality.',
          duration: 5000
        });
        return;
      }

      // Close the draft selection panel if it's open
      setShowDraftPanel(false);

      // Immediately show the draft viewer with loading state
      setActiveDraft({ draft_id: draftId, title: 'Loading...', content: '' });
      setDraftContent('');
      setDraftTitle('Loading...');
      setLoadingDrafts(true);

      try {
        // Load the draft
        const draft = await draftApi.getDraft(draftId);
        setActiveDraft(draft);
        setDraftContent(draft.content || '');
        setDraftTitle(draft.title);
        localStorage.removeItem('open_draft_id');
      } catch (error) {
        console.error('[ChatArea] Failed to open draft:', error);
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to open draft',
          duration: 3000
        });
        setActiveDraft(null);
        localStorage.removeItem('open_draft_id');
      } finally {
        setLoadingDrafts(false);
      }
    };

    // Check on mount for any pending draft to open
    const pendingDraftId = localStorage.getItem('open_draft_id');
    if (pendingDraftId) {
      handleOpenDraft();
    }

    window.addEventListener('open-draft', handleOpenDraft);
    return () => {
      window.removeEventListener('open-draft', handleOpenDraft);
    };
  }, [isPremium, addNotification]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
    };
  }, []);

  // Listen for paste-question event from onboarding wizard
  useEffect(() => {
    const handlePasteQuestion = (event: CustomEvent) => {
      const { question } = event.detail;
      setMessage(question);
      // Focus the textarea after a brief delay to ensure the wizard has closed
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    };

    window.addEventListener('paste-question', handlePasteQuestion as EventListener);
    return () => {
      window.removeEventListener('paste-question', handlePasteQuestion as EventListener);
    };
  }, []);

  // Fetch custom prompts on mount
  useEffect(() => {
    const fetchCustomPrompts = async () => {
      try {
        const response = await userApi.fetchPrompts();
        if (response.prompts) {
          setCustomPrompts(response.prompts);
        }
      } catch (error) {
        console.error('Failed to fetch custom prompts:', error);
      }
    };

    fetchCustomPrompts();
  }, []);

  // Monitor chat input container width for responsive button sizing
  useEffect(() => {
    const container = chatInputContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        // Use 900px threshold - condense buttons when container is narrower than this
        setCompactButtons(width < 890);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Handle saving custom prompt
  const handleSaveCustomPrompt = async (label: string, prompt: string, availableIn: string) => {
    try {
      if (editingPrompt && editingPrompt.id) {
        await userApi.updatePrompt(editingPrompt.id, label, prompt, availableIn);
      } else {
        await userApi.savePrompt(label, prompt, availableIn);
      }

      // Refresh custom prompts
      const fetchResponse = await userApi.fetchPrompts();
      if (fetchResponse.prompts) {
        setCustomPrompts(fetchResponse.prompts);
      }

      // Reset editing state
      setEditingPrompt(null);
    } catch (error) {
      console.error('Failed to save custom prompt:', error);
      throw error;
    }
  };

  // Handle editing custom prompt
  const handleEditPrompt = (promptId: string, label: string, prompt: string, availableIn?: string) => {
    setEditingPrompt({ id: promptId, label, prompt, available_in: availableIn });
    setShowCustomPromptModal(true);
  };

  // Handle deleting custom prompt
  const handleDeletePrompt = async (promptId: string, promptLabel: string) => {
    try {
      await userApi.deletePrompt(promptId);

      // Close the quick prompts menu
      setShowQuickPromptsMenu(false);

      // Show delete notification
      addNotification({
        type: 'success',
        title: 'Prompt Deleted',
        message: `"${promptLabel}" has been deleted`,
        duration: 3000,
      });

      // Refresh custom prompts
      const fetchResponse = await userApi.fetchPrompts();
      if (fetchResponse.prompts) {
        setCustomPrompts(fetchResponse.prompts);
      }
    } catch (error) {
      console.error('Failed to delete custom prompt:', error);
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: 'Failed to delete prompt',
        duration: 3000,
      });
    }
  };

  // Handle image file selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if user has premium access
    if (!isPremium) {
      // Reset the file input
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
      addNotification({
        type: 'warning',
        title: 'Premium Feature',
        message: 'Image attachments are a premium-only feature. Please upgrade your account to access this functionality.',
        duration: 5000
      });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      addNotification({
        type: 'error',
        title: 'Invalid File',
        message: 'Please select an image file',
        duration: 3000
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      addNotification({
        type: 'error',
        title: 'File Too Large',
        message: 'Image must be less than 10MB',
        duration: 3000
      });
      return;
    }

    setAttachedImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Remove attached image
  const handleRemoveImage = () => {
    setAttachedImage(null);
    setImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleSend = async (submittedMessage?: unknown) => {
    const input = typeof submittedMessage === 'string' ? submittedMessage : message;
    if ((!input.trim() && taskAttachments.length === 0 && !attachedImage) || isLoading || isGenerating || sendPendingRef.current) return;
    sendPendingRef.current = true;
    setIsLoading(true);

    const userMessage = input.trim();
    const restoreInput = () => {
      setMessage(userMessage);
      taskAttachments.forEach(attachment => useSessionStore.getState().addTaskAttachment(attachment));
      if (attachedImage) {
        setAttachedImage(attachedImage);
        setImagePreview(imagePreview);
      }
    };

    // Build the complete message content including task attachments
    let completeMessage = userMessage;
    if (taskAttachments.length > 0) {
      const attachmentTexts = taskAttachments.map(attachment => {
        const header = `[Task Result: ${attachment.taskName}#${attachment.id}#${attachment.taskType}${attachment.documentId ? ` | Document ID: ${attachment.documentId}` : ''}]`;
        return `\n\n${header}\n${attachment.content}`;
      }).join('\n\n');
      completeMessage = userMessage + attachmentTexts;
    }

    // Store sent task results for later retrieval (for attachments not yet sent)
    if (taskAttachments.length > 0) {
      storeSentTaskResults(taskAttachments);
    }

    // Upload image to S3 if attached
    let imageS3Key: string | undefined;
    if (attachedImage) {
      setUploadingImage(true);
      try {
        const uploadResult = await chatApi.uploadImage(attachedImage);
        imageS3Key = uploadResult.s3_key;
        console.log('Image uploaded to S3:', imageS3Key);
      } catch (error) {
        console.error('Failed to upload image:', error);
        addNotification({
          type: 'error',
          title: 'Upload Failed',
          message: 'Failed to upload image. Please try again.',
          duration: 3000
        });
        setUploadingImage(false);
        setIsLoading(false);
        sendPendingRef.current = false;
        return;
      }
      setUploadingImage(false);
    }

    // Add user message to chat (use completeMessage so content is embedded)
    addChatMessage({
      role: 'user',
      content: completeMessage,
      receiveTime: Date.now(),
      image_s3_key: imageS3Key,
    } as any);

    setMessage('');
    clearTaskAttachments();
    handleRemoveImage();

    // Send the complete message to backend
    setIsLoading(true);

    try {
      // Send message to API (with complete message including task attachments and image)
      const response = await chatApi.sendMessage(
        completeMessage,
        chatMessages,
        undefined, // userInfo
        undefined, // sessionId
        imageS3Key // image S3 key
      );

      // Check if this is an async response that requires polling
      if (response.status === 'processing' && response.message_id) {
        const messageId = response.message_id;

        // Switch from "Sending" to "Generating" state
        setIsLoading(false);
        setIsGenerating(true);
        localStorage.setItem(PENDING_MSG_KEY, messageId);

        // Start polling for the response
        pollingIntervalRef.current = setInterval(async () => {
          try {
            const messageResponse = await chatApi.getMessage(messageId);

            // Debug: Log the response to see what we're getting
            console.log('[CHAT POLLING] Message response:', messageResponse);
            console.log('[CHAT POLLING] Processing status:', messageResponse.processing_status);
            console.log('[CHAT POLLING] Processing status history:', messageResponse.processing_status_history);

            // Track processing status updates from history array (new method)
            if (messageResponse.processing_status_history && Array.isArray(messageResponse.processing_status_history)) {
              console.log('[CHAT POLLING] Received status history with', messageResponse.processing_status_history.length, 'items');

              // Find new statuses that haven't been queued yet (check against ref, not state)
              const newStatuses = messageResponse.processing_status_history.filter(statusUpdate =>
                !queuedStatusTimestampsRef.current.has(statusUpdate.timestamp)
              );

              if (newStatuses.length > 0) {
                console.log('[CHAT POLLING] Found', newStatuses.length, 'new status updates to queue for display');

                // Mark these statuses as queued immediately to prevent duplicates on next poll
                newStatuses.forEach(statusUpdate => {
                  queuedStatusTimestampsRef.current.add(statusUpdate.timestamp);
                });

                // Add new statuses one by one with 1-second delay between each
                newStatuses.forEach((statusUpdate, index) => {
                  setTimeout(() => {
                    const newStatus = {
                      status: statusUpdate.status,
                      message: statusUpdate.details?.message || '',
                      timestamp: statusUpdate.timestamp,
                      documents: statusUpdate.details?.documents,
                      resources: statusUpdate.details?.resources,
                      classification: statusUpdate.details?.classification
                    };
                    console.log('[CHAT POLLING] Adding status with', index, 'second delay:', newStatus);
                    setProcessingStatuses(current => [...current, newStatus]);
                  }, index * 1000); // 1 second delay between each status
                });
              }
            }
            // Fallback to old single-status method if history not available
            else if (messageResponse.processing_status) {
              console.log('[CHAT POLLING] Using fallback single status:', messageResponse.processing_status);
              setProcessingStatuses(prev => {
                const exists = prev.some(s => s.status === messageResponse.processing_status!.status && s.timestamp === messageResponse.processing_status!.timestamp);
                if (!exists) {
                  const newStatus = {
                    status: messageResponse.processing_status!.status,
                    message: messageResponse.processing_status!.details?.message || '',
                    timestamp: messageResponse.processing_status!.timestamp,
                    documents: messageResponse.processing_status!.details?.documents,
                    resources: messageResponse.processing_status!.details?.resources,
                    classification: messageResponse.processing_status!.details?.classification
                  };
                  return [...prev, newStatus];
                }
                return prev;
              });
            }

            // As above: a turn waiting on the attorney, not on a model.
            if (messageResponse.image_proposal?.status === 'asked') {
              setDrawingAsk({ messageId, drawing: messageResponse.image_proposal });
              setProcessingStatuses([]);
              return;
            }

            if (messageResponse.message &&
                messageResponse.message !== 'null' &&
                messageResponse.message !== null) {

              // Stop polling
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              localStorage.removeItem(PENDING_MSG_KEY);

              setIsGenerating(false);

              // Save status history from backend response (not from state, to avoid race conditions with setTimeout)
              if (messageResponse.processing_status_history && messageResponse.processing_status_history.length > 0) {
                console.log('[CHAT] Saving status history for message:', messageId, 'with', messageResponse.processing_status_history.length, 'statuses');
                console.log('[CHAT] Raw status history from backend:', JSON.stringify(messageResponse.processing_status_history, null, 2));
                const formattedStatuses = messageResponse.processing_status_history.map(statusUpdate => ({
                  status: statusUpdate.status,
                  message: statusUpdate.details?.message || '',
                  timestamp: statusUpdate.timestamp,
                  documents: statusUpdate.details?.documents,
                  resources: statusUpdate.details?.resources,
                  classification: statusUpdate.details?.classification
                }));

                console.log('[CHAT] Formatted statuses:', JSON.stringify(formattedStatuses, null, 2));

                setCompletedStatusHistories(prev => {
                  const newMap = new Map(prev);
                  newMap.set(messageId, formattedStatuses);
                  console.log('[CHAT] Status history saved. Map now has', newMap.size, 'entries');
                  console.log('[CHAT] Saved with messageId:', messageId);
                  console.log('[CHAT] Map keys:', Array.from(newMap.keys()));
                  return newMap;
                });
              } else {
                // No history came back with the answer, so keep what was shown while it
                // was being generated. Clearing the live list without saving anything is
                // what made the steps disappear when the response arrived.
                console.log('[CHAT] No status history from backend; keeping the live steps');
                setProcessingStatuses(current => {
                  if (current.length > 0) {
                    setCompletedStatusHistories(prev => {
                      const newMap = new Map(prev);
                      newMap.set(messageId, current);
                      return newMap;
                    });
                  }
                  return current;
                });
              }

              setProcessingStatuses([]); // Clear statuses for next message
              queuedStatusTimestampsRef.current.clear(); // Clear queued status tracking for next message

              // Add assistant message to chat
              const assistantMessage = {
                role: 'assistant' as const,
                content: messageResponse.message,
                receiveTime: Date.now(),
                userMessageId: messageId,
                message_context: messageResponse.message_context,
                session_context: messageResponse.session_context,
                token_usage: messageResponse.token_usage,
                // A drawing this turn offered to make, for the card under the answer.
                image_proposal: messageResponse.image_proposal,
              };
              console.log('[CHAT] Adding assistant message with userMessageId:', messageId);
              console.log('[CHAT] Assistant message object:', assistantMessage);
              addChatMessage(assistantMessage);

              // Update session context if present
              updateSessionContext(messageResponse.session_context);

              // Trigger credit update
              window.dispatchEvent(new Event('credits-updated'));

              // Auto-save session after message
              debouncedAutoSave();
            }
          } catch (error) {
            console.error('Error polling for message:', error);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            localStorage.removeItem(PENDING_MSG_KEY);
            setIsGenerating(false);
          }
        }, 3500); // Poll every 3.5 seconds
      } else if (response.message) {
        // Synchronous response
        setIsLoading(false);
        addChatMessage({
          role: 'assistant',
          content: response.message,
          receiveTime: Date.now(),
          message_context: response.message_context,
          session_context: response.session_context,
          token_usage: response.token_usage,
          image_proposal: (response as any).image_proposal,
        });
        updateSessionContext(response.session_context);

        // Trigger credit update
        window.dispatchEvent(new Event('credits-updated'));

        // Auto-save session after message
        debouncedAutoSave();
      } else if (response.error) {
        setIsLoading(false);
        restoreInput();
        addChatMessage({
          role: 'assistant',
          content: response.error || 'An error occurred. Please try again.',
          receiveTime: Date.now(),
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      restoreInput();
      setIsLoading(false);
      setIsGenerating(false);
      addChatMessage({
        role: 'assistant',
        content: 'Failed to send message. Please try again.',
        receiveTime: Date.now(),
      });
    } finally {
      sendPendingRef.current = false;
    }
  };

  // Highlight search matches in text — only operates on text nodes, never inside HTML tags
  const highlightSearchText = (text: string, messageId: number): string => {
    if (!searchQuery.trim()) return text;

    const currentResultMessageId = searchResults[currentSearchIndex]?.messageId;
    const isCurrentMatch = messageId === currentResultMessageId;

    const highlightClass = isCurrentMatch
      ? 'bg-yellow-300 text-gray-900 font-semibold search-highlight'
      : 'bg-yellow-100 text-gray-900 search-highlight';

    const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matchRegex = new RegExp(`(${escapedQuery})`, 'gi');

    // Split on HTML tags so we never inject markup inside attribute values
    return text.split(/(<[^>]+>)/).map(segment => {
      if (segment.startsWith('<')) return segment; // HTML tag — pass through untouched
      return segment.replace(matchRegex, (match) =>
        `<span class="${highlightClass}">${match}</span>`
      );
    }).join('');
  };

  // Perform the actual search
  const performSearch = (query: string, force: boolean = false) => {
    if (!query.trim()) {
      setSearchQuery('');
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    const results = messages
      .filter((msg) => msg.type === 'user' || msg.type === 'assistant')
      .filter((msg) => msg.content.toLowerCase().includes(query.toLowerCase()))
      .map((msg) => ({
        messageId: msg.id,
        content: msg.content,
        type: msg.type,
        timestamp: msg.timestamp
      }));

    // If more than 50 matches and not forced, show warning
    if (results.length > 50 && !force) {
      setPendingSearchQuery(query);
      setPendingMatchCount(results.length);
      setShowSearchWarning(true);
      return;
    }

    // Only NOW set searchQuery to trigger highlighting
    setSearchQuery(query);
    setSearchResults(results);
    setCurrentSearchIndex(0);

    // Scroll to first result (with delay to allow highlighting to render)
    if (results.length > 0) {
      setTimeout(() => {
        const messageElement = document.querySelector(`[data-message-id="${results[0].messageId}"]`);
        if (messageElement) {
          // Find the highlighted mark element within this message
          const highlightedMark = messageElement.querySelector('span.search-highlight');
          if (highlightedMark) {
            highlightedMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 150);
    }
  };

  // Search chat messages with debounce
  const handleSearch = (query: string) => {
    setSearchInputValue(query);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchQuery('');
      setSearchResults([]);
      setCurrentSearchIndex(0);
      return;
    }

    // Debounce the search by 500ms
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query);
    }, 500);
  };

  // Proceed with search despite large result count
  const proceedWithSearch = () => {
    setShowSearchWarning(false);
    setSearchInputValue(pendingSearchQuery);
    performSearch(pendingSearchQuery, true);
  };

  // Cancel the pending search
  const cancelSearch = () => {
    setShowSearchWarning(false);
    setSearchInputValue('');
    setSearchQuery('');
    setPendingSearchQuery('');
    setPendingMatchCount(0);
    setSearchResults([]);
    setCurrentSearchIndex(0);
  };

  const navigateSearchResults = (direction: 'next' | 'prev') => {
    if (searchResults.length === 0) return;

    let newIndex;
    if (direction === 'next') {
      newIndex = (currentSearchIndex + 1) % searchResults.length;
    } else {
      newIndex = (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
    }
    setCurrentSearchIndex(newIndex);

    // Scroll to the actual highlighted match within the message
    setTimeout(() => {
      const messageId = searchResults[newIndex].messageId;
      const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
      if (messageElement) {
        // Find the highlighted mark element within this message (current match has bg-yellow-300)
        const highlightedMark = messageElement.querySelector('span.search-highlight');
        if (highlightedMark) {
          highlightedMark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fallback to message element
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 50);
  };

  // Download chat as Word document
  const downloadChatAsWord = async () => {
    // Convert markdown text to paragraphs for docx
    const convertTextToParagraphs = (text: string, isUser: boolean): Paragraph[] => {
      // Strip resource tags first
      const cleanText = stripResourceTags(text);
      const paragraphs: Paragraph[] = [];
      const lines = cleanText.split('\n');

      let i = 0;
      while (i < lines.length) {
        const line = lines[i];

        // Handle code blocks
        if (line.startsWith('```')) {
          const codeLines: string[] = [];
          i++;
          while (i < lines.length && !lines[i].startsWith('```')) {
            codeLines.push(lines[i]);
            i++;
          }
          codeLines.forEach(codeLine => {
            paragraphs.push(new Paragraph({
              children: [
                new TextRun({
                  text: codeLine,
                  font: 'Courier New',
                  size: 20,
                  shading: { fill: 'F0F0F0' }
                })
              ],
              spacing: { before: 100, after: 100 }
            }));
          });
          i++;
          continue;
        }

        // Handle headers
        if (line.startsWith('### ')) {
          paragraphs.push(new Paragraph({
            text: line.substring(4),
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 240, after: 120 }
          }));
        } else if (line.startsWith('## ')) {
          paragraphs.push(new Paragraph({
            text: line.substring(3),
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 280, after: 140 }
          }));
        } else if (line.startsWith('# ')) {
          paragraphs.push(new Paragraph({
            text: line.substring(2),
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 320, after: 160 }
          }));
        }
        // Handle bullet lists
        else if (line.match(/^[\*\-] /)) {
          paragraphs.push(new Paragraph({
            text: line.substring(2),
            bullet: { level: 0 },
            spacing: { before: 60, after: 60 }
          }));
        }
        // Handle numbered lists
        else if (line.match(/^\d+\. /)) {
          const text = line.replace(/^\d+\. /, '');
          paragraphs.push(new Paragraph({
            text: text,
            numbering: { reference: 'default-numbering', level: 0 },
            spacing: { before: 60, after: 60 }
          }));
        }
        // Handle regular text with inline formatting
        else if (line.trim()) {
          const children: TextRun[] = [];
          let currentText = line;

          // Simple inline formatting (bold, italic, code)
          // Note: This is a simplified approach - a full markdown parser would be more robust
          const parts = currentText.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/g);

          parts.forEach(part => {
            if (part.startsWith('**') && part.endsWith('**')) {
              children.push(new TextRun({ text: part.slice(2, -2), bold: true }));
            } else if (part.startsWith('*') && part.endsWith('*')) {
              children.push(new TextRun({ text: part.slice(1, -1), italics: true }));
            } else if (part.startsWith('`') && part.endsWith('`')) {
              children.push(new TextRun({
                text: part.slice(1, -1),
                font: 'Courier New',
                shading: { fill: 'F0F0F0' }
              }));
            } else if (part) {
              children.push(new TextRun({ text: part }));
            }
          });

          paragraphs.push(new Paragraph({
            children: children.length > 0 ? children : [new TextRun({ text: line })],
            spacing: { before: 100, after: 100 }
          }));
        }
        // Empty line
        else {
          paragraphs.push(new Paragraph({ text: '' }));
        }

        i++;
      }

      return paragraphs;
    };

    // Build document sections
    const documentChildren: Paragraph[] = [];

    // Add title
    documentChildren.push(new Paragraph({
      text: 'Chat Export',
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 }
    }));

    // Add export date
    documentChildren.push(new Paragraph({
      text: `Exported on ${new Date().toLocaleString()}`,
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }));

    // Add messages
    messages.forEach((msg) => {
      if (msg.type === 'user' || msg.type === 'assistant') {
        // Add message header
        documentChildren.push(new Paragraph({
          children: [
            new TextRun({
              text: `${msg.type === 'user' ? 'You' : 'Assistant'} - ${msg.timestamp}`,
              bold: true,
              size: 18,
              color: '666666'
            })
          ],
          spacing: { before: 300, after: 120 },
          shading: { fill: msg.type === 'user' ? 'E3F2FD' : 'F5F5F5' }
        }));

        // Add message content
        const contentParagraphs = convertTextToParagraphs(msg.content, msg.type === 'user');
        contentParagraphs.forEach(para => {
          documentChildren.push(para);
        });

        // Add spacing after message
        documentChildren.push(new Paragraph({ text: '', spacing: { after: 200 } }));
      }
    });

    // Create document
    const doc = new Document({
      sections: [{
        properties: {},
        children: documentChildren
      }],
      numbering: {
        config: [{
          reference: 'default-numbering',
          levels: [{
            level: 0,
            format: 'decimal',
            text: '%1.',
            alignment: AlignmentType.LEFT
          }]
        }]
      }
    });

    // Generate and download
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-export-${new Date().toISOString().slice(0, 10)}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Memoize markdown overrides to prevent infinite re-renders
  const markdownOverrides = useMemo(() => ({
    span: {
      component: (props: any) => {
        if (props.className === 'rsrc-link') {
          return <ResourceLink {...props} onDocumentClick={stableOnDocumentClick} activeDocuments={allActiveDocuments} />;
        }
        // Handle document tags
        if (props.className === 'doc-tag') {
          const docType = props['data-doc-type'];
          const titleCaseType = toTitleCaseDocType(docType);
          // Color code by document type: patents=teal, publications=blue, others=gray
          const colorClass = docType === 'PATENT'
            ? 'bg-teal-100 text-teal-900 hover:bg-teal-200'
            : docType === 'PUBLICATION'
            ? 'bg-blue-100 text-blue-900 hover:bg-blue-200'
            : 'bg-gray-100 text-gray-900 hover:bg-gray-200';

          return (
            <span
              onClick={() => handleTagClick(props['data-doc-type'], props['data-doc-id'])}
              className={`inline-flex items-center ${colorClass} px-2 py-0.5 rounded text-xs font-medium not-italic cursor-pointer transition-colors not-prose`}
              style={{ pointerEvents: 'auto' }}
              title={`Click to view ${titleCaseType} ${props['data-doc-id']}`}
            >
              <span style={{ pointerEvents: 'none' }}>{titleCaseType} {props['data-doc-id']}</span>
            </span>
          );
        }
        // Handle task tags
        if (props.className === 'task-tag') {
          const taskId = props['data-task-id'];
          const taskName = props['data-task-name'];
          return (
            <span
              onClick={() => {
                const currentSentTaskResults = useSessionStore.getState().sentTaskResults;
                if (taskId && currentSentTaskResults[taskId]) {
                  setSelectedTaskResult(currentSentTaskResults[taskId]);
                  setShowTaskSidePane(true);
                }
              }}
              className="inline-flex items-center gap-1 bg-purple-100 text-purple-900 px-2 py-0.5 rounded text-xs font-medium not-italic not-prose cursor-pointer hover:bg-purple-200 transition-colors"
              style={{ pointerEvents: 'auto' }}
              title={`Click to view ${taskName} result`}
            >
              <FileText className="w-3 h-3" style={{ pointerEvents: 'none' }} />
              <span style={{ pointerEvents: 'none' }}>{taskName}</span>
            </span>
          );
        }
        return <span {...props} />;
      },
    },
  }), [stableOnDocumentClick, handleTagClick, setSelectedTaskResult, setShowTaskSidePane, allActiveDocuments]);

  const isEmptyChat = chatMessages.length === 0 || (chatMessages.length === 1 && chatMessages[0].role === 'assistant' && chatMessages[0].content.startsWith('Welcome to Patent Loop!'));
  const availableChatDocuments = getActiveDocuments();

  return (
    <div className={`flex ${activeDraft && hasOpenDocuments ? 'flex-col' : ''} h-full bg-white overflow-hidden relative ${isDraggingResize ? `select-none ${hasOpenDocuments ? 'cursor-row-resize' : 'cursor-col-resize'}` : ''}`}>
      {/* Main Chat Area */}
      <div className={`flex flex-col ${activeDraft && hasOpenDocuments ? 'flex-1 min-h-0 overflow-hidden' : activeDraft ? 'flex-1 min-w-0' : 'h-full'} relative ${!hasOpenDocuments ? 'w-full' : 'flex-1 min-w-0'}`}>
        {/* Floating Chat Settings / Search */}
        <div className="absolute top-2 right-4 z-10 flex gap-2">
        {/* Search Overlay */}
        {showSearchDialog && (
          <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 p-3 w-[400px]">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchInputValue}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchResults.length > 0) {
                      e.preventDefault();
                      navigateSearchResults('next');
                    }
                  }}
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }
                  setShowSearchDialog(false);
                  setSearchInputValue('');
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                  className="h-7 w-7 p-0"
                  aria-label="Close conversation search"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {searchQuery && (
              <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                <span>
                  {searchResults.length > 0
                    ? `${currentSearchIndex + 1} of ${searchResults.length} results`
                    : `No results`}
                </span>
                {searchResults.length > 0 && (
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateSearchResults('prev')}
                      className="h-6 w-6 p-0"
                      disabled={searchResults.length === 0}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateSearchResults('next')}
                      className="h-6 w-6 p-0"
                      disabled={searchResults.length === 0}
                    >
                      ↓
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Document Selection Button */}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 h-7 px-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100/80 bg-white/60 backdrop-blur-sm rounded-md shadow-sm"
          title="Select documents/references"
          onClick={() => setShowDocumentPanel(true)}
        >
          <BookOpen className="w-3.5 h-3.5" />
        </Button>

        {/* Draft Selection Button */}
        <Button
          variant="ghost"
          size="sm"
          className={`gap-1.5 h-7 px-2 hover:bg-gray-100/80 bg-white/60 backdrop-blur-sm rounded-md shadow-sm ${
            activeDraft ? 'text-purple-600 hover:text-purple-700' : 'text-gray-400 hover:text-gray-600'
          }`}
          title={activeDraft ? `Active Draft: ${activeDraft.title}` : isPremium ? "Select draft" : "Select draft (Premium Feature)"}
          onClick={() => {
            if (!isPremium) {
              addNotification({
                type: 'warning',
                title: 'Premium Feature',
                message: 'Drafts is a premium-only feature. Please upgrade your account to access this functionality.',
                duration: 5000
              });
              return;
            }
            setShowDraftPanel(true);
          }}
        >
          <FileEdit className="w-3.5 h-3.5" />
        </Button>

        <ChatSettingsMenu iconOnly onSearch={() => setShowSearchDialog(true)} onExport={downloadChatAsWord} onLanguage={() => setShowLanguageModal(true)} onHelp={() => setShowChatInfoModal(true)} onFeedback={() => setShowFeedbackModal(true)} />
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className={compactMode ? 'px-3 py-3' : 'px-6 py-6'}>
            <div className={`${compactMode ? 'space-y-3' : 'space-y-6'}`}>
          {isEmptyChat && (
            <div className="mx-auto max-w-xl py-12">
              <h1 className="text-2xl font-semibold text-slate-900">What would you like to work on?</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">Add documents for analysis, ask a question, or pick up a previous conversation.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={onOpenDocumentModal} className="gap-2"><Plus className="h-4 w-4" />Add a patent or document</Button>
                <Button variant="outline" onClick={() => document.querySelector<HTMLElement>('[data-chat-composer="true"]')?.focus()}>Ask a question</Button>
                <Button variant="outline" onClick={() => window.dispatchEvent(new CustomEvent('navigate-page', { detail: { page: 'sessions' } }))}>Resume a recent session</Button>
              </div>
            </div>
          )}
          {!isEmptyChat && messages.map((msg) => (
            <div key={msg.id} className="space-y-2" data-message-id={msg.id}>
              {msg.type === 'system' && (
                <div className="flex justify-center">
                  <div className={`max-w-[800px] bg-blue-50 text-blue-700 rounded-full ${compactMode ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'}`}>
                    {msg.content}
                  </div>
                </div>
              )}

              {msg.type === 'user' && (
                <div
                  className={`flex justify-end group ${compactMode ? 'gap-1' : 'gap-2'}`}
                  onMouseEnter={() => setHoveredMessageId(msg.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >
                  <div className={`flex items-start opacity-0 group-hover:opacity-100 transition-opacity ${compactMode ? 'gap-1' : 'gap-2'}`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`${copiedMessageId === msg.id ? (compactMode ? 'h-auto px-1.5' : 'h-auto px-2') : (compactMode ? 'h-6 w-6 p-0' : 'h-8 w-8 p-0')} hover:bg-gray-100`}
                      onClick={() => handleCopyMessage(msg.content, msg.id)}
                      title="Copy message"
                    >
                      {copiedMessageId === msg.id ? (
                        <span className={`${compactMode ? 'text-[10px]' : 'text-xs'} text-green-600 font-medium`}>Copied</span>
                      ) : (
                        <Copy className={compactMode ? 'w-3 h-3 text-gray-600' : 'w-4 h-4 text-gray-600'} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={compactMode ? 'h-6 w-6 p-0 hover:bg-purple-100' : 'h-8 w-8 p-0 hover:bg-purple-100'}
                      onClick={() => handleCreatePromptFromMessage(msg.content)}
                      title="Create quick prompt"
                    >
                      <Sparkles className={compactMode ? 'w-3 h-3 text-purple-600' : 'w-4 h-4 text-purple-600'} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={compactMode ? 'h-6 w-6 p-0 hover:bg-red-100' : 'h-8 w-8 p-0 hover:bg-red-100'}
                      onClick={() => handleDeleteMessage(msg.id)}
                      title="Delete message"
                    >
                      <Trash2 className={compactMode ? 'w-3 h-3 text-red-600' : 'w-4 h-4 text-red-600'} />
                    </Button>
                  </div>
                  <div className={`max-w-[800px] bg-blue-600 text-white rounded-2xl rounded-tr-sm ${compactMode ? 'px-3 py-2' : 'px-4 py-3'}`}>
                    <div className="prose prose-invert chat-user-text" style={fontSizeStyle}>
                      {parseMessageTags(msg.content).map((part, index) => {
                        if (part.type === 'tag' && part.docType && part.docId) {
                          // Determine color based on document type for sent messages
                          let tagColorClass = 'bg-blue-500 hover:bg-blue-400';
                          if (part.docType === 'FOREIGN_PATENT') {
                            tagColorClass = 'bg-teal-500 hover:bg-teal-400';
                          } else if (part.docType === 'UPLOAD') {
                            tagColorClass = 'bg-amber-500 hover:bg-amber-400';
                          } else if (part.docType === 'MPEP') {
                            tagColorClass = 'bg-emerald-500 hover:bg-emerald-400';
                          } else if (part.docType === 'CFR') {
                            tagColorClass = 'bg-cyan-500 hover:bg-cyan-400';
                          } else if (part.docType === 'USC') {
                            tagColorClass = 'bg-violet-500 hover:bg-violet-400';
                          } else if (part.docType === 'DRAFT') {
                            tagColorClass = 'bg-purple-500 hover:bg-purple-400';
                          } else if (part.docType === 'PROS_DOC') {
                            tagColorClass = 'bg-orange-500 hover:bg-orange-400';
                          }

                          const titleCaseType = toTitleCaseDocType(part.docType);

                          // For pros docs, look up the name from session context
                          let displayText = `${titleCaseType} ${part.docId}`;
                          if (part.docType === 'PROS_DOC') {
                            try {
                              const sessionContextStr = localStorage.getItem('session_context');
                              if (sessionContextStr) {
                                const context = JSON.parse(sessionContextStr);
                                const prosDocIds = context.pros_docs || [];
                                const prosDocNames = context.pros_doc_names || [];
                                const index = prosDocIds.indexOf(part.docId);
                                if (index !== -1 && prosDocNames[index]) {
                                  displayText = prosDocNames[index];
                                }
                              }
                            } catch (e) {
                              console.error('Error looking up pros doc name:', e);
                            }
                          }

                          return (
                            <span
                              key={index}
                              onClick={() => handleTagClick(part.docType!, part.docId!)}
                              className={`inline-flex items-center ${tagColorClass} text-white px-2 py-0.5 rounded text-xs font-medium cursor-pointer transition-colors not-prose`}
                              title={`Click to view ${titleCaseType} ${part.docId}`}
                            >
                              {displayText}
                            </span>
                          );
                        } else if (part.type === 'taskTag' && part.taskName) {
                          return (
                            <span
                              key={index}
                              onClick={() => {
                                // Create task result from extracted content
                                if (part.taskContent && part.taskType) {
                                  const taskResult: TaskResultAttachment = {
                                    id: part.taskId || '',
                                    taskName: part.taskName,
                                    taskType: part.taskType,
                                    content: part.taskContent,
                                    timestamp: new Date().toISOString(),
                                    documentId: part.docId
                                  };
                                  setSelectedTaskResult(taskResult);
                                  setShowTaskSidePane(true);
                                } else {
                                  console.warn('No task content or type found for:', part.taskName);
                                }
                              }}
                              className="inline-flex items-center gap-1 bg-purple-500 text-white px-2 py-0.5 rounded text-xs font-medium not-prose cursor-pointer hover:bg-purple-600 transition-colors"
                              title={`Click to view ${part.taskName} result`}
                            >
                              <FileText className="w-3 h-3" />
                              {part.docId ? `${part.taskName} (Doc: ${part.docId})` : part.taskName}
                            </span>
                          );
                        }
                        // Apply search highlighting to text parts
                        const highlightedContent = searchQuery.trim()
                          ? highlightSearchText(part.content, msg.id)
                          : part.content;
                        return <span key={index} dangerouslySetInnerHTML={{ __html: highlightedContent }} />;
                      })}
                    </div>
                    {/* Display attached image if present */}
                    {(msg as any).image_s3_key && (
                      <AttachedImage s3Key={(msg as any).image_s3_key} />
                    )}
                    <div className={`text-blue-100 ${compactMode ? 'text-[10px] mt-0.5' : 'text-xs mt-1'}`}>{msg.timestamp}</div>
                  </div>
                </div>
              )}

              {msg.type === 'assistant' && (
                <>
                  {/* What was done to answer this message, kept after the answer
                      arrives. The steps used to vanish the moment the response landed:
                      this block was disabled, and the live list is cleared for the next
                      message, so the record of which documents were read went with it. */}
                  {(() => {
                    const messageId = (msg as any).userMessageId;
                    const statuses = messageId ? completedStatusHistories.get(messageId) : null;
                    const isExpanded = messageId ? expandedStatusHistories.has(messageId) : false;

                    if (statuses && statuses.length > 0) {
                      return (
                        <div className="flex justify-start mb-2">
                          <Collapsible
                            open={isExpanded}
                            onOpenChange={() => {
                              setExpandedStatusHistories(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(messageId)) {
                                  newSet.delete(messageId);
                                } else {
                                  newSet.add(messageId);
                                }
                                return newSet;
                              });
                            }}
                            className="max-w-2xl w-full"
                          >
                            <CollapsibleTrigger className="w-full">
                              <div className={`flex items-center gap-2 px-3 py-1.5 text-xs text-gray-600 cursor-pointer transition-colors hover:text-gray-800 ${isExpanded ? 'bg-gray-50 rounded-t-lg' : 'rounded-lg'}`}>
                                {isExpanded ? (
                                  <ChevronDown className="w-3 h-3" />
                                ) : (
                                  <ChevronRight className="w-3 h-3" />
                                )}
                                <span>Processing steps ({statuses.length})</span>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="space-y-1 px-3 py-2 bg-gray-50 rounded-b-lg">
                                {statuses.map((status, idx) => (
                                  <div key={idx} className="flex items-start gap-2">
                                    <Check className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1">
                                      <div className="text-xs text-gray-700">{status.message}</div>
                                      {status.documents && status.documents.length > 0 && (() => {
                                        // Deduplicate documents (e.g., "Foreign Patent EP1267036A1" appearing twice)
                                        const uniqueDocs = Array.from(new Set(status.documents));
                                        return (
                                          <div className="text-[10px] text-gray-600 mt-0.5">
                                            Using: {uniqueDocs.join(', ')}
                                          </div>
                                        );
                                      })()}
                                      {status.resources && status.resources.length > 0 && (() => {
                                        // Deduplicate resources
                                        const uniqueResources = Array.from(new Set(status.resources));
                                        return (
                                          <div className="text-[10px] text-gray-600 mt-0.5">
                                            Resources: {uniqueResources.join(', ')}
                                          </div>
                                        );
                                      })()}
                                      {status.classification && status.classification.length > 0 && (() => {
                                        // Filter out "unsure_response" and clean up commas
                                        const filtered = status.classification
                                          .filter((item: string) => item.trim() !== 'unsure_response')
                                          .map((item: string) => item.trim())
                                          .filter((item: string) => item.length > 0);
                                        return filtered.length > 0 ? (
                                          <div className="text-[10px] text-gray-500 mt-0.5 italic">
                                            {filtered.join(', ')}
                                          </div>
                                        ) : null;
                                      })()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  <AssistantMessage
                    msg={msg}
                    compactMode={compactMode}
                    fontSizeStyle={fontSizeStyle}
                    messageFeedback={messageFeedback}
                    feedbackToasts={feedbackToasts}
                    hoveredMessageId={hoveredMessageId}
                    onDocumentClick={stableOnDocumentClick}
                    preprocessAssistantMessage={preprocessAssistantMessage}
                    markdownOverrides={markdownOverrides}
                    onThumbsUp={handleSendPositiveFeedback}
                    onThumbsDown={handleSendNegativeFeedback}
                    onDeleteMessage={handleDeleteMessage}
                    onCopyMessage={handleCopyMessage}
                    copiedMessageId={copiedMessageId}
                    setHoveredMessageId={setHoveredMessageId}
                    activeDraft={activeDraft}
                    activeDraftsInChat={activeDraftsInChat}
                    setPendingDraftContent={setPendingDraftContent}
                    setShowDraftModDialog={setShowDraftModDialog}
                    stripResourceTags={stripResourceTags}
                    searchQuery={searchQuery}
                    isCurrentSearchResult={searchResults[currentSearchIndex]?.messageId === msg.id}
                    onImageProposal={handleImageProposal}
                  />
                </>
              )}
            </div>
          ))}

              {/* A turn waiting to be told whether to draw something. Shown instead of
                  the thinking indicator, because nothing is being thought about yet. */}
              {drawingAsk && (
                <div className="flex justify-start">
                  <ChatDrawingAsk
                    drawing={drawingAsk.drawing}
                    onDecide={handleDrawingDecision}
                  />
                </div>
              )}

              {/* Loading Indicator with Status Updates */}
              {!drawingAsk && (isLoading || isGenerating) && (
                <div className="flex justify-start">
                  <div className="space-y-3 max-w-2xl">
                    <div className="text-gray-900 px-4 py-3">
                      {/* Sequential Status Display */}
                      {processingStatuses.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          {processingStatuses.map((status, idx) => {
                            const isLast = idx === processingStatuses.length - 1;
                            return (
                              <div key={idx} className="flex items-start gap-2">
                                {isLast ? (
                                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin mt-0.5 flex-shrink-0" />
                                ) : (
                                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                )}
                                <div className="flex-1">
                                  <div className={`text-sm ${isLast ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
                                    {status.message}
                                  </div>
                                  {status.documents && status.documents.length > 0 && (() => {
                                    // Deduplicate documents (e.g., "Foreign Patent EP1267036A1" appearing twice)
                                    const uniqueDocs = Array.from(new Set(status.documents));
                                    return (
                                      <div className="text-xs text-gray-600 mt-1">
                                        Using: {uniqueDocs.join(', ')}
                                      </div>
                                    );
                                  })()}
                                  {(status as any).resources && (status as any).resources.length > 0 && (() => {
                                    // Deduplicate resources
                                    const uniqueResources = Array.from(new Set((status as any).resources));
                                    return (
                                      <div className="text-xs text-gray-600 mt-1">
                                        Resources: {uniqueResources.join(', ')}
                                      </div>
                                    );
                                  })()}
                                  {(status as any).classification && (status as any).classification.length > 0 && (() => {
                                    // Filter out "unsure_response" and clean up commas
                                    const filtered = (status as any).classification
                                      .filter((item: string) => item.trim() !== 'unsure_response')
                                      .map((item: string) => item.trim())
                                      .filter((item: string) => item.length > 0);
                                    return filtered.length > 0 ? (
                                      <div className="text-xs text-gray-500 mt-1 italic">
                                        {filtered.join(', ')}
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-10">
                          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                        </div>
                      )}
                      {showDelayMessage && (
                        <div className="text-xs text-gray-600 text-center mt-2 max-w-[300px]">
                          Taking longer than expected. Please wait as your response is being generated...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Input Area */}
      <div className={`flex-shrink-0 border-t border-gray-200 bg-white ${compactMode ? 'p-1' : 'p-2 pb-0'}`}>
        <div ref={chatInputContainerRef} className="relative">
          {/* Mention Dropdown */}
          {showMentionDropdown && filteredDocuments.length > 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50">
              <div className="max-h-64 overflow-y-auto">
                {(() => {
                  console.log('[@ MENU] Rendering filteredDocuments:', filteredDocuments);
                  console.log('[@ MENU] Foreign patents in filtered:', filteredDocuments.filter(d => d.type === 'Foreign Patent'));
                  return null;
                })()}
                {filteredDocuments.map((doc, index) => {
                  const Icon = getDocumentIcon(doc.type);
                  return (
                    <div
                      key={`${doc.type}-${doc.id}`}
                      onMouseDown={(e) => {
                        e.preventDefault(); // Prevent textarea from losing focus
                        console.log('[@ MENU CLICK] Document clicked:', doc);
                        handleMentionSelect(doc);
                      }}
                      className={`px-3 py-2 cursor-pointer transition-colors ${
                        index === selectedMentionIndex
                          ? 'bg-blue-50 border-l-2 border-blue-600'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {doc.title}
                          </div>
                          <div className="text-xs text-gray-500">{doc.type}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">↑↓</kbd> navigate ·
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs ml-1">Tab</kbd> or
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs ml-1">Enter</kbd> select ·
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs ml-1">Esc</kbd> cancel
              </div>
            </div>
          )}

          {showMentionDropdown && filteredDocuments.length === 0 && (
            <div className="absolute bottom-full left-0 mb-2 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50">
              <div className="text-sm text-gray-500 text-center">
                No documents/resources found matching "{mentionFilter}". Add documents/resources to Session documents to the left.
              </div>
            </div>
          )}

          {/* Image Preview */}
          {imagePreview && (
            <div className="mb-3 relative inline-block">
              <img
                src={imagePreview}
                alt="Attached image preview"
                className="max-h-40 rounded-lg border border-gray-300"
              />
              <button
                onClick={handleRemoveImage}
                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                title="Remove image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Task Result Attachments */}
          {taskAttachments.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {taskAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 border border-purple-300 rounded-lg text-sm cursor-pointer hover:bg-purple-200 transition-colors group"
                  onClick={() => {
                    setSelectedTaskResult(attachment);
                    setShowTaskSidePane(true);
                  }}
                  title={`Click to view ${attachment.taskName} result`}
                >
                  <FileText className="w-4 h-4 text-purple-700 flex-shrink-0" />
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium text-purple-900 truncate max-w-[200px]">
                      {attachment.taskName}
                    </span>
                    <span className="text-xs text-purple-600">
                      {new Date(attachment.timestamp).toLocaleString()}
                    </span>
                    {attachment.documentId && (
                      <span className="text-xs text-purple-700 truncate max-w-[200px]">
                        Document: {attachment.documentId}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTaskAttachment(attachment.id);
                    }}
                    className="ml-1 text-purple-600 hover:text-purple-800 hover:bg-purple-200 rounded p-0.5 transition-colors flex-shrink-0"
                    title="Remove attachment"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <LexicalChatInput
            documents={allActiveDocuments}
            value={message}
            placeholder="Ask about your documents…"
            onSubmit={handleSend}
            onChange={setMessage}
            disabled={isLoading || isGenerating}
            onOpenDocumentModal={onOpenDocumentModal}
          />
          <p id="chat-composer-help" className="px-3 py-1.5 text-xs text-slate-500">@ to reference a document · Enter to send · Shift+Enter for a new line</p>

          <div className="bg-gray-50 rounded-lg border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs text-slate-600" aria-label={`Manage ${availableChatDocuments.length} session documents`}>
                      <FileText className="h-4 w-4" />
                      <span>{availableChatDocuments.length} document{availableChatDocuments.length !== 1 ? 's' : ''}</span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-80 max-h-80 overflow-y-auto">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-slate-900">Available in this session</p>
                      <p className="mt-1 text-xs text-slate-500">Use @ to explicitly reference a document. Sources used are shown with each response; an open preview does not change them.</p>
                    </div>
                    {availableChatDocuments.length === 0 ? <p className="px-3 py-2 text-xs text-slate-500">No documents yet. You can still ask a general question.</p> : availableChatDocuments.map(doc => (
                      <div key={doc.type + '-' + doc.id} className="flex items-start gap-2 px-3 py-2 text-sm">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                        <span className="min-w-0 break-words">{doc.title}</span>
                      </div>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={onOpenDocumentModal}><Plus className="mr-2 h-4 w-4" />Add documents</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setShowDocumentPanel(true)}><BookOpen className="mr-2 h-4 w-4" />Manage documents and references</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex items-center gap-2">
                {/* Image Attachment Button */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 h-9"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={isLoading || isGenerating || uploadingImage}
                  title={isPremium ? "Attach image" : "Attach image (Premium Feature)"}
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4" />
                  )}
                  {!compactButtons && <span className="text-xs">{uploadingImage ? 'Uploading...' : 'Image'}</span>}
                </Button>

                {/* Quick Actions Dropdown - Always shown to allow custom prompts */}
                <DropdownMenu open={showQuickPromptsMenu} onOpenChange={setShowQuickPromptsMenu}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 h-9"
                        disabled={isLoading || isGenerating}
                        title="Quick Prompts"
                      >
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        {!compactButtons && <span className="text-xs">Quick Prompts</span>}
                        {!compactButtons && <ChevronDown className="w-3 h-3" />}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-80 max-h-[500px] overflow-y-auto">
                      {/* Create Custom Prompt Button */}
                      <div className="px-2 py-2 border-b border-gray-200 sticky top-0 bg-white z-10">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full gap-2 justify-start"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPrompt(null);
                            setShowCustomPromptModal(true);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                          Create Custom Prompt
                        </Button>
                      </div>

                      {/* User Custom Prompts */}
                      {chatPrompts.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Your Custom Prompts
                          </div>
                          {chatPrompts.map((customPrompt) => (
                            <DropdownMenuItem
                              key={customPrompt.prompt_id}
                              onClick={() => handleQuickAction({
                                category: 'Custom',
                                icon: Sparkles,
                                label: customPrompt.label,
                                color: 'text-purple-600',
                                prompt: customPrompt.prompt
                              })}
                              className="flex items-start gap-3 py-2 cursor-pointer group"
                            >
                              <Sparkles className="w-4 h-4 mt-0.5 text-purple-600 flex-shrink-0" />
                              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-sm font-medium">{customPrompt.label}</span>
                                <span className="text-xs text-gray-500 line-clamp-2">{customPrompt.prompt}</span>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 hover:bg-blue-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditPrompt(customPrompt.prompt_id, customPrompt.label, customPrompt.prompt, customPrompt.available_in);
                                  }}
                                  title="Edit prompt"
                                >
                                  <Edit className="w-3 h-3 text-blue-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 hover:bg-red-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePrompt(customPrompt.prompt_id, customPrompt.label);
                                  }}
                                  title="Delete prompt"
                                >
                                  <Trash2 className="w-3 h-3 text-red-600" />
                                </Button>
                              </div>
                            </DropdownMenuItem>
                          ))}
                          <div className="border-t border-gray-200 my-1" />
                        </>
                      )}

                      {/* Built-in Prompts */}
                      {Object.entries(groupedActions).map(([category, actions], categoryIndex) => (
                        <div key={categoryIndex}>
                          {categoryIndex > 0 && <div className="border-t border-gray-200 my-1" />}
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            {category}
                          </div>
                          {actions.map((action, actionIndex) => (
                            <DropdownMenuItem
                              key={`${categoryIndex}-${actionIndex}`}
                              onClick={() => handleQuickAction(action)}
                              className="flex items-start gap-3 py-2 cursor-pointer"
                            >
                              <action.icon className={`w-4 h-4 mt-0.5 ${action.color} flex-shrink-0`} />
                              <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium">{action.label}</span>
                                <span className="text-xs text-gray-500 line-clamp-2">{action.prompt}</span>
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                <ChatSettingsMenu onSearch={() => setShowSearchDialog(true)} onExport={downloadChatAsWord} onLanguage={() => setShowLanguageModal(true)} onHelp={() => setShowChatInfoModal(true)} onFeedback={() => setShowFeedbackModal(true)} />

                {/* Send becomes Stop while a turn is in flight, where every chat puts
                    it. It does not stop the worker — the answer is generated off a queue
                    and finishes whether anyone is listening — so what it stops is the
                    waiting, and the message goes back in the box to be sent again or
                    reworded. That is the only useful thing it can do: there is no partial
                    answer here to keep. */}
                {isLoading || isGenerating ? (
                  <Button
                    onClick={() => stopWaiting(true)}
                    size="sm"
                    variant="outline"
                    className="gap-2"
                    title="Stop waiting for this answer and put the message back in the box"
                  >
                    <Square className="w-3.5 h-3.5 fill-current" />
                    {!compactButtons && <span>Stop</span>}
                  </Button>
                ) : (
                  <Button
                    onClick={handleSend}
                    disabled={(!message.trim() && taskAttachments.length === 0 && !attachedImage) || uploadingImage}
                    size="sm"
                    className="gap-2"
                    title="Send message"
                  >
                    <Send className="w-4 h-4" />
                    {!compactButtons && <span>Send</span>}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteMessage}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteMessage}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
      />

      {/* Chat Info Modal */}
      <Dialog open={showChatInfoModal} onOpenChange={setShowChatInfoModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>How the Chat Works</DialogTitle>
            <DialogDescription>
              Every prompt is paired with the most relevant materials so answers stay grounded in your patent file.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 text-sm text-gray-600">
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                {
                  icon: <Sparkles className="w-5 h-5 text-purple-600" />,
                  title: 'Understands intent',
                  body: 'We read your prompt plus recent history to learn what you need.'
                },
                {
                  icon: <FileText className="w-5 h-5 text-blue-600" />,
                  title: 'Selects documents',
                  body: 'Relevant files are pulled into the prompt automatically—watch the tags above the text area.'
                },
                {
                  icon: <Send className="w-5 h-5 text-emerald-600" />,
                  title: 'Sends combined context',
                  body: 'Your words + cited documents are sent together to craft a grounded response.'
                }
              ].map((card, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gradient-to-b from-white to-gray-50">
                  <div className="h-10 w-10 rounded-full bg-white border border-gray-200 flex items-center justify-center mb-3 shadow-sm">
                    {card.icon}
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1">{card.title}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className="bg-white text-blue-700 border-blue-200">
                  Session documents
                </Badge>
                <span className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Source material</span>
              </div>
              <p>
                The items shown under <strong>Session documents</strong> are inspected first when determining what documents/resources 
                to use for the input when composing a reply. You can mention specific documents/resources of the Session documents by 
                typing @ and then selecting the particular document with enter or tab.
              </p>
            </div>

            <p className="text-xs text-gray-500">
              Tip: hover a tag to confirm which document or task result is being attached before sending your message.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Prompt Modal */}
      <CustomPromptModal
        open={showCustomPromptModal}
        onOpenChange={async (open) => {
          setShowCustomPromptModal(open);
          if (!open) {
            setEditingPrompt(null);
            // Refresh prompts when modal closes to ensure latest data
            try {
              const response = await userApi.fetchPrompts();
              if (response.prompts) {
                setCustomPrompts(response.prompts);
              }
            } catch (error) {
              console.error('Failed to refresh prompts on modal close:', error);
            }
          }
        }}
        onSave={handleSaveCustomPrompt}
        editingPrompt={editingPrompt}
      />

      {/* Draft Modification Dialog */}
      <DraftModificationDialog
        isOpen={showDraftModDialog}
        onClose={() => {
          setShowDraftModDialog(false);
          setPendingDraftContent('');
        }}
        onConfirm={async (method, lineNumber) => {
          if (!activeDraft) return;

          const newContent = await modifyDraft(
            activeDraft.draft_id,
            draftContent,
            pendingDraftContent,
            method,
            lineNumber
          );

          if (newContent) {
            // Update local state with the new content
            setDraftContent(newContent);

            // Update the active draft in activeDraftsInChat to keep it in sync
            setActiveDraftsInChat(prevDrafts =>
              prevDrafts.map(draft =>
                draft.draft_id === activeDraft.draft_id
                  ? { ...draft, content: newContent }
                  : draft
              )
            );

            // Close the dialog
            setShowDraftModDialog(false);
            setPendingDraftContent('');
          }
          // If newContent is null, modifyDraft failed and showed an error notification
          // Keep the dialog open for retry
        }}
        currentContent={draftContent}
        newContent={pendingDraftContent}
        draftTitle={draftTitle || activeDraft?.title || 'Untitled Draft'}
        initialLineNumber={selectedDraftLine}
      />

      {/* Language Selector Modal */}
      <Dialog open={showLanguageModal} onOpenChange={setShowLanguageModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Chat Language</DialogTitle>
            <DialogDescription>
              Choose the language for the AI assistant to respond in
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-4">
            <div className="grid grid-cols-2 gap-2">
              {LANGUAGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setChatLanguage(option.value);
                    setShowLanguageModal(false);
                  }}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left hover:bg-gray-50 ${
                    chatLanguage === option.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <Languages className="w-5 h-5 flex-shrink-0 text-gray-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{option.label}</div>
                    {option.nativeName && (
                      <div className="text-sm text-gray-500 truncate">{option.nativeName}</div>
                    )}
                  </div>
                  {chatLanguage === option.value && (
                    <span className="text-blue-600 font-bold">✓</span>
                  )}
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Search Warning Dialog */}
      <AlertDialog open={showSearchWarning} onOpenChange={setShowSearchWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Large Number of Search Results</AlertDialogTitle>
            <AlertDialogDescription>
              This search found <strong>{pendingMatchCount}</strong> matches. Highlighting this many results may cause the browser to become slow or freeze.
              <br /><br />
              Do you want to proceed anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelSearch}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={proceedWithSearch}>
              Proceed Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Task Result Side Pane */}
      {showTaskSidePane && selectedTaskResult && (
        <div className="fixed inset-y-0 right-0 w-[600px] bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <FileText className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-semibold text-gray-900 truncate">{selectedTaskResult.taskName}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {new Date(selectedTaskResult.timestamp).toLocaleString()}
                  </p>
                  {selectedTaskResult.documentId && (
                    <p className="text-xs text-purple-600 mt-1">
                      Document ID: {selectedTaskResult.documentId}
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setShowTaskSidePane(false);
                  setSelectedTaskResult(null);
                }}
                className="flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              {renderTaskResultDisplay(selectedTaskResult)}
            </div>
          </div>
        </div>
      )}

      {/* Document Selection Panel - Right Side Sliding Panel */}
      {showDocumentPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowDocumentPanel(false)}
          />

          {/* Sliding Panel */}
          <div className="fixed right-0 top-0 h-full w-[500px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Panel Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Select Documents</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDocumentPanel(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4">
                  <Tabs defaultValue="my-docs" className="w-full">
                    <TabsList className="grid w-full grid-cols-8">
                      <TabsTrigger value="my-docs">Apps</TabsTrigger>
                      <TabsTrigger value="patent">Patent</TabsTrigger>
                      <TabsTrigger value="publication">Pub</TabsTrigger>
                      <TabsTrigger value="foreign_patent">Foreign</TabsTrigger>
                      <TabsTrigger value="upload">Upload</TabsTrigger>
                      <TabsTrigger value="mpep">MPEP</TabsTrigger>
                      <TabsTrigger value="cfr">CFR</TabsTrigger>
                      <TabsTrigger value="usc">USC</TabsTrigger>
                    </TabsList>

                    <TabsContent value="my-docs" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <div className="text-xs font-medium text-gray-500 mb-2">ACTIVE DOCUMENTS</div>

                        {/* Currently Selected Application */}
                        {selectedAppData && (
                          <div className="space-y-2">
                            <div className="text-xs font-semibold text-gray-700 uppercase">Current Application</div>
                            <div className="space-y-1">
                              <div className="w-full border border-gray-200 rounded p-2 bg-gray-50">
                                <div className="flex flex-col items-start w-full">
                                  <div className="font-medium text-sm">
                                    {selectedAppData.applicationNumber || selectedApplicationNumber}
                                  </div>
                                  <div className="text-xs text-gray-600 line-clamp-2">
                                    {selectedAppData.inventionTitle}
                                  </div>
                                </div>
                              </div>

                              {/* Show associated patent/publication from metadata */}
                              {(selectedAppData?.patentNumber || selectedAppData?.earliestPublicationNumber) && (
                                <div className="ml-4 space-y-1">
                                  {selectedAppData.patentNumber && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-1 text-xs"
                                      title={selectedAppData.inventionTitle || selectedAppData.patentNumber}
                                      onClick={() => {
                                        onDocumentClick({
                                          file_name: selectedAppData.patentNumber,
                                          resource_id: selectedAppData.patentNumber,
                                          file_type: 'patent',
                                          metadata: selectedAppData
                                        });
                                      }}
                                    >
                                      <div className="flex items-center gap-2 w-full">
                                        <FileText className="w-3 h-3 text-blue-500" />
                                        <span className="text-xs text-gray-700">Patent: {selectedAppData.patentNumber}</span>
                                      </div>
                                    </Button>
                                  )}
                                  {selectedAppData.earliestPublicationNumber && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="w-full justify-start text-left h-auto py-1 text-xs"
                                      title={selectedAppData.inventionTitle || selectedAppData.earliestPublicationNumber}
                                      onClick={() => {
                                        onDocumentClick({
                                          file_name: selectedAppData.earliestPublicationNumber,
                                          resource_id: selectedAppData.earliestPublicationNumber,
                                          file_type: 'publication',
                                          metadata: selectedAppData
                                        });
                                      }}
                                    >
                                      <div className="flex items-center gap-2 w-full">
                                        <FileText className="w-3 h-3 text-green-500" />
                                        <span className="text-xs text-gray-700">Pub: {selectedAppData.earliestPublicationNumber}</span>
                                      </div>
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="text-xs font-medium text-gray-500 mb-2">SESSION DOCUMENTS</div>
                        <ScrollArea className="h-[400px]">
                          <div className="space-y-3 pr-4">
                            {/* Session Applications with their Documents */}
                            {sessionApplications && sessionApplications.length > 0 && (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <Database className="w-3 h-3 text-purple-600" />
                                  <p className="text-xs font-semibold text-gray-700 uppercase">Session Applications ({sessionApplications.filter((app: any) => {
                                    const appData = app.applicationMetaData || app.application?.applicationMetaData;
                                    return appData?.inventionTitle;
                                  }).length})</p>
                                </div>
                                {sessionApplications.map((app: any, index: number) => {
                                  const appData = app.applicationMetaData || app.application?.applicationMetaData;
                                  const appId = app.applicationNumberText || app.application_id || app.application?.applicationNumberText;
                                  const isActive = appId === selectedApplicationNumber;

                                  // Skip if no title
                                  if (!appData?.inventionTitle) return null;

                                  if (isActive) return null; // Skip active (shown above)

                                  // Get associated patent and publication from the app metadata
                                  const hasPatent = appData?.patentNumber;
                                  const hasPublication = appData?.earliestPublicationNumber;

                                  return (
                                    <div key={appId || index} className="space-y-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        title={appData?.inventionTitle || appId}
                                        onClick={() => {
                                          onDocumentClick({
                                            type: 'application',
                                            file_type: 'application',
                                            resource_id: `application-${appId}`,
                                            title: appId,
                                            application_id: appId,
                                            application: app.application || app,
                                            applicationMetaData: appData
                                          });
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="font-medium text-sm">
                                            {appId}
                                          </div>
                                          <div className="text-xs text-gray-600 line-clamp-2 mt-1">
                                            {appData?.inventionTitle || 'No title available'}
                                          </div>
                                          {appData?.filingDate && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              Filed: {appData.filingDate}
                                            </div>
                                          )}
                                        </div>
                                      </Button>

                                      {/* Show associated patent/publication from metadata */}
                                      {(hasPatent || hasPublication) && (
                                        <div className="ml-4 space-y-1">
                                          {hasPatent && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="w-full justify-start text-left h-auto py-1 text-xs"
                                              title={appData.inventionTitle || appData.patentNumber}
                                              onClick={() => {
                                                onDocumentClick({
                                                  file_name: appData.patentNumber,
                                                  resource_id: appData.patentNumber,
                                                  file_type: 'patent',
                                                  metadata: appData
                                                });
                                              }}
                                            >
                                              <div className="flex items-center gap-2 w-full">
                                                <FileText className="w-3 h-3 text-blue-500" />
                                                <span className="text-xs text-gray-700">Patent: {appData.patentNumber}</span>
                                              </div>
                                            </Button>
                                          )}
                                          {hasPublication && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="w-full justify-start text-left h-auto py-1 text-xs"
                                              title={appData.inventionTitle || appData.earliestPublicationNumber}
                                              onClick={() => {
                                                onDocumentClick({
                                                  file_name: appData.earliestPublicationNumber,
                                                  resource_id: appData.earliestPublicationNumber,
                                                  file_type: 'publication',
                                                  metadata: appData
                                                });
                                              }}
                                            >
                                              <div className="flex items-center gap-2 w-full">
                                                <FileText className="w-3 h-3 text-green-500" />
                                                <span className="text-xs text-gray-700">Pub: {appData.earliestPublicationNumber}</span>
                                              </div>
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Prosecution Documents */}
                            {(() => {
                              const prosDocuments = (sessionDocuments as any[])?.filter(
                                (doc: any) => doc.resource_type === 'pros_doc' || doc.resource_type === 'prosecution_document'
                              ) || [];

                              if (prosDocuments.length === 0) return null;

                              return (
                                <div className="space-y-2 mt-4">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-3 h-3 text-orange-600" />
                                    <p className="text-xs font-semibold text-gray-700 uppercase">Prosecution Documents ({prosDocuments.length})</p>
                                  </div>
                                  <div className="space-y-1">
                                    {prosDocuments.map((doc: any, index: number) => (
                                      <Button
                                        key={doc.resource_id || index}
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        title={doc.resource_name || doc.file_name}
                                        onClick={() => {
                                          onDocumentClick(doc);
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="flex items-center gap-2 w-full">
                                            <FileText className="w-4 h-4 flex-shrink-0 text-orange-600" />
                                            <div className="font-medium text-sm truncate flex-1">
                                              {doc.resource_name || doc.file_name}
                                            </div>
                                          </div>
                                          <div className="text-xs text-gray-500 mt-1">
                                            {doc.resource_id}
                                          </div>
                                        </div>
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Active Drafts */}
                            {activeDraftsInChat.length > 0 && (
                              <div className="space-y-2 mt-4">
                                <div className="text-xs font-semibold text-gray-700 uppercase flex items-center justify-between">
                                  <span>Active Drafts</span>
                                  <Badge variant="outline" className="text-xs">
                                    {activeDraftsInChat.length}
                                  </Badge>
                                </div>
                                <div className="space-y-1">
                                  {activeDraftsInChat.map((draft) => (
                                    <div
                                      key={draft.draft_id}
                                      className="w-full border border-purple-200 rounded p-2 bg-purple-50 flex items-center justify-between"
                                    >
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <FileEdit className="w-4 h-4 text-purple-600 flex-shrink-0" />
                                        <div className="flex flex-col min-w-0 flex-1">
                                          <div className="font-medium text-sm text-gray-900 truncate">
                                            {draft.title}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            Draft
                                          </div>
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-purple-600 hover:text-red-600 hover:bg-red-50"
                                        onClick={() => removeDraftFromActiveDocuments(draft.draft_id)}
                                        title="Remove from session documents"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Empty State */}
                            {(!sessionApplications || sessionApplications.length === 0) && activeDraftsInChat.length === 0 && (
                              <div className="text-sm text-gray-500 text-center py-8">
                                <File className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                <p>No documents available</p>
                                <p className="text-xs mt-1">Documents will appear here once loaded</p>
                              </div>
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    </TabsContent>

                    <TabsContent value="patent" className="space-y-4 mt-4">
                      {(() => {
                        const patentDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'patent'
                        ) || [];

                        // Get active patents from session context
                        const activePatents = sessionContext?.current_patents || [];

                        // Use loaded patents if available, otherwise use session patents
                        const basePatentDocs = patientsLoaded ? allPatentDocs : patentDocs;

                        // Add active patents that aren't in the list yet
                        const combinedPatentDocs = [...basePatentDocs];
                        activePatents.forEach((patentId: string) => {
                          if (!patentId) return;
                          const cleanPatentId = patentId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                          const exists = basePatentDocs.some((doc: any) => {
                            const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanDoc === cleanPatentId || doc.resource_id === patentId;
                          });
                          if (!exists) {
                            combinedPatentDocs.push({
                              resource_id: patentId,
                              file_name: patentId,
                              file_type: 'patent',
                              fromActiveContext: true
                            });
                          }
                        });

                        // Sort with active patents from session context first
                        const sortedDocs = [...combinedPatentDocs].sort((a, b) => {
                          const aIsInActiveContext = activePatents.some((p: string) => {
                            const cleanA = a.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanA === cleanP || a.resource_id === p;
                          });
                          const bIsInActiveContext = activePatents.some((p: string) => {
                            const cleanB = b.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanB === cleanP || b.resource_id === p;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!patientsLoaded && (
                              <Button
                                onClick={loadAllPatents}
                                disabled={loadingPatents}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingPatents ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Patents...
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Load All Patents
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isInActiveContext = activePatents.some((p: string) => {
                                      const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                      const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                      return cleanDoc === cleanP || doc.resource_id === p;
                                    });
                                    return (
                                      <Button
                                        key={doc.resource_id}
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        title={doc.title || doc.metadata?.title || doc.file_name}
                                        onClick={() => {
                                          onDocumentClick(doc);
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="flex items-center gap-2 w-full">
                                            <FileText className="w-4 h-4 flex-shrink-0" />
                                            <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                            {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                          </div>
                                          {(doc.title || doc.metadata?.title) && (
                                            <div className="text-xs text-gray-600 mt-1 ml-6 line-clamp-2">
                                              {doc.title || doc.metadata?.title}
                                            </div>
                                          )}
                                          {doc.upload_date && (
                                            <div className="text-xs text-gray-500 mt-1 ml-6">
                                              Added: {new Date(doc.upload_date).toLocaleDateString()}
                                            </div>
                                          )}
                                        </div>
                                      </Button>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-8">
                                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-600">No patents in session</p>
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="publication" className="space-y-4 mt-4">
                      {(() => {
                        const publicationDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'publication'
                        ) || [];

                        const activePublications = sessionContext?.current_publications || [];
                        const basePublicationDocs = publicationsLoaded ? allPublicationDocs : publicationDocs;

                        const combinedPublicationDocs = [...basePublicationDocs];
                        activePublications.forEach((publicationId: string) => {
                          if (!publicationId) return;
                          const cleanPublicationId = publicationId.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                          const exists = basePublicationDocs.some((doc: any) => {
                            const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanDoc === cleanPublicationId || doc.resource_id === publicationId;
                          });
                          if (!exists) {
                            combinedPublicationDocs.push({
                              resource_id: publicationId,
                              file_name: publicationId,
                              file_type: 'publication',
                              fromActiveContext: true
                            });
                          }
                        });

                        const sortedDocs = [...combinedPublicationDocs].sort((a, b) => {
                          const aIsInActiveContext = activePublications.some((p: string) => {
                            const cleanA = a.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanA === cleanP || a.resource_id === p;
                          });
                          const bIsInActiveContext = activePublications.some((p: string) => {
                            const cleanB = b.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                            return cleanB === cleanP || b.resource_id === p;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!publicationsLoaded && (
                              <Button
                                onClick={loadAllPublications}
                                disabled={loadingPublications}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingPublications ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Publications...
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Load All Publications
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isInActiveContext = activePublications.some((p: string) => {
                                      const cleanDoc = doc.file_name?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                      const cleanP = p?.replace(/^(Patent|Publication|Application)\s+/i, '').trim();
                                      return cleanDoc === cleanP || doc.resource_id === p;
                                    });
                                    return (
                                      <Button
                                        key={doc.resource_id}
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        title={doc.title || doc.metadata?.title || doc.file_name}
                                        onClick={() => {
                                          onDocumentClick(doc);
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="flex items-center gap-2 w-full">
                                            <FileText className="w-4 h-4 flex-shrink-0" />
                                            <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                            {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                          </div>
                                          {(doc.title || doc.metadata?.title) && (
                                            <div className="text-xs text-gray-600 mt-1 ml-6 line-clamp-2">
                                              {doc.title || doc.metadata?.title}
                                            </div>
                                          )}
                                          {doc.upload_date && (
                                            <div className="text-xs text-gray-500 mt-1 ml-6">
                                              Added: {new Date(doc.upload_date).toLocaleDateString()}
                                            </div>
                                          )}
                                        </div>
                                      </Button>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-8">
                                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-600">No publications in session</p>
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="upload" className="space-y-4 mt-4">
                      {(() => {
                        const uploadDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'upload'
                        ) || [];

                        const activeUploads = sessionContext?.resource_names || [];
                        const baseUploadDocs = uploadsLoaded ? allUploadDocs : uploadDocs;

                        const combinedUploadDocs = [...baseUploadDocs];
                        activeUploads.forEach((uploadName: string) => {
                          if (!uploadName) return;
                          const exists = baseUploadDocs.some((doc: any) => {
                            return doc.file_name === uploadName || doc.resource_id === uploadName;
                          });
                          if (!exists) {
                            combinedUploadDocs.push({
                              resource_id: uploadName,
                              file_name: uploadName,
                              file_type: 'upload',
                              fromActiveContext: true
                            });
                          }
                        });

                        const sortedDocs = [...combinedUploadDocs].sort((a, b) => {
                          const aIsInActiveContext = activeUploads.some((u: string) => {
                            return a.file_name === u || a.resource_id === u;
                          });
                          const bIsInActiveContext = activeUploads.some((u: string) => {
                            return b.file_name === u || b.resource_id === u;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!uploadsLoaded && (
                              <Button
                                onClick={loadAllUploads}
                                disabled={loadingUploads}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingUploads ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Uploads...
                                  </>
                                ) : (
                                  <>
                                    <File className="w-4 h-4 mr-2" />
                                    Load All Uploads
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isInActiveContext = activeUploads.some((u: string) => {
                                      return doc.file_name === u || doc.resource_id === u;
                                    });
                                    return (
                                      <Button
                                        key={doc.resource_id}
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        title={doc.file_name}
                                        onClick={() => {
                                          onDocumentClick(doc);
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="flex items-center gap-2 w-full">
                                            <File className="w-4 h-4 flex-shrink-0" />
                                            <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                            {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                          </div>
                                          {doc.upload_date && (
                                            <div className="text-xs text-gray-500 mt-1 ml-6">
                                              Uploaded: {new Date(doc.upload_date).toLocaleDateString()}
                                            </div>
                                          )}
                                          {doc.file_size && (
                                            <div className="text-xs text-gray-500 ml-6">
                                              {(doc.file_size / 1024).toFixed(1)} KB
                                            </div>
                                          )}
                                        </div>
                                      </Button>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-8">
                                    <File className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-600">No uploaded documents in session</p>
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="foreign_patent" className="space-y-4 mt-4">
                      {(() => {
                        const foreignPatentDocs = (sessionDocuments as any[])?.filter((doc: any) =>
                          doc.file_type === 'foreign_patent'
                        ) || [];

                        const activeForeignPatents = sessionContext?.current_foreign_patents || [];
                        const baseForeignPatentDocs = foreignPatientsLoaded ? allForeignPatentDocs : foreignPatentDocs;

                        const combinedForeignPatentDocs = [...baseForeignPatentDocs];
                        activeForeignPatents.forEach((foreignPatentId: string) => {
                          if (!foreignPatentId) return;
                          const exists = baseForeignPatentDocs.some((doc: any) => {
                            return doc.file_name === foreignPatentId || doc.resource_id === foreignPatentId;
                          });
                          if (!exists) {
                            combinedForeignPatentDocs.push({
                              resource_id: foreignPatentId,
                              file_name: foreignPatentId,
                              file_type: 'foreign_patent',
                              fromActiveContext: true
                            });
                          }
                        });

                        const sortedDocs = [...combinedForeignPatentDocs].sort((a, b) => {
                          const aIsInActiveContext = activeForeignPatents.some((p: string) => {
                            return a.file_name === p || a.resource_id === p;
                          });
                          const bIsInActiveContext = activeForeignPatents.some((p: string) => {
                            return b.file_name === p || b.resource_id === p;
                          });

                          if (aIsInActiveContext && !bIsInActiveContext) return -1;
                          if (!aIsInActiveContext && bIsInActiveContext) return 1;
                          return 0;
                        });

                        return (
                          <div className="space-y-3">
                            {!foreignPatientsLoaded && (
                              <Button
                                onClick={loadAllForeignPatents}
                                disabled={loadingForeignPatents}
                                variant="outline"
                                className="w-full"
                              >
                                {loadingForeignPatents ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Loading All Foreign Patents...
                                  </>
                                ) : (
                                  <>
                                    <FileText className="w-4 h-4 mr-2" />
                                    Load All Foreign Patents
                                  </>
                                )}
                              </Button>
                            )}
                            <ScrollArea className="h-[500px]">
                              <div className="space-y-2 pr-4">
                                {sortedDocs.length > 0 ? (
                                  sortedDocs.map((doc: any) => {
                                    const isInActiveContext = activeForeignPatents.some((p: string) => {
                                      return doc.file_name === p || doc.resource_id === p;
                                    });
                                    return (
                                      <Button
                                        key={doc.resource_id}
                                        variant="outline"
                                        size="sm"
                                        className="w-full justify-start text-left h-auto py-2"
                                        title={doc.title || doc.metadata?.title || doc.file_name}
                                        onClick={() => {
                                          onDocumentClick(doc);
                                        }}
                                      >
                                        <div className="flex flex-col items-start w-full">
                                          <div className="flex items-center gap-2 w-full">
                                            <FileText className="w-4 h-4 flex-shrink-0" />
                                            <div className="font-medium text-sm truncate">{doc.file_name}</div>
                                            {isInActiveContext && <Badge className="ml-auto bg-blue-100 text-blue-800">Active</Badge>}
                                          </div>
                                          {(doc.title || doc.metadata?.title) && (
                                            <div className="text-xs text-gray-600 mt-1 ml-6 line-clamp-2">
                                              {doc.title || doc.metadata?.title}
                                            </div>
                                          )}
                                          {doc.upload_date && (
                                            <div className="text-xs text-gray-500 mt-1 ml-6">
                                              Added: {new Date(doc.upload_date).toLocaleDateString()}
                                            </div>
                                          )}
                                        </div>
                                      </Button>
                                    );
                                  })
                                ) : (
                                  <div className="text-center py-8">
                                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-600">No foreign patents in session</p>
                                  </div>
                                )}
                              </div>
                            </ScrollArea>
                          </div>
                        );
                      })()}
                    </TabsContent>

                    <TabsContent value="mpep" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        {/* Direct section input */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Load Specific Section</label>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="Enter MPEP section (e.g., 2141)"
                              className="flex-1"
                              value={mpepSectionInput}
                              onChange={(e) => setMpepSectionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleLoadMpepSection(mpepSectionInput);
                                }
                              }}
                            />
                            <Button
                              onClick={() => handleLoadMpepSection(mpepSectionInput)}
                              variant="default"
                              disabled={loadingMpepSection || !mpepSectionInput.trim()}
                            >
                              {loadingMpepSection ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Load'
                              )}
                            </Button>
                          </div>
                        </div>

                        <Separator />

                        {/* Browse MPEP structure */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Browse MPEP</label>
                          {loadingMpepStructure ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            </div>
                          ) : mpepStructure && Object.keys(mpepStructure).length > 0 ? (
                            <ScrollArea className="h-[400px]">
                              <div className="space-y-1 pr-4">
                                {(() => {
                                  const chapters = Object.keys(mpepStructure)
                                    .filter(key => /^\d{4}$/.test(key) && key.endsWith('00'))
                                    .sort((a, b) => parseInt(a) - parseInt(b))
                                    .map(chapterKey => {
                                      const chapterData = mpepStructure[chapterKey];
                                      const chapterNum = parseInt(chapterKey).toString();

                                      const sectionNumbers = chapterData.sections
                                        ? Object.keys(chapterData.sections).sort((a, b) => parseInt(a) - parseInt(b))
                                        : [];

                                      return {
                                        id: chapterNum,
                                        title: chapterData.title || `Chapter ${chapterNum}`,
                                        sections: sectionNumbers
                                      };
                                    });

                                  return chapters.map((chapter) => {
                                    const hasSections = chapter.sections && chapter.sections.length > 0;
                                    return (
                                      <Collapsible
                                        key={chapter.id}
                                        open={expandedMpepChapters.has(chapter.id)}
                                        onOpenChange={() => toggleMpepChapter(chapter.id)}
                                      >
                                        <CollapsibleTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="w-full justify-start text-left h-auto py-2"
                                          >
                                            <ChevronDown
                                              className={`w-4 h-4 mr-2 transition-transform ${
                                                expandedMpepChapters.has(chapter.id) ? '' : '-rotate-90'
                                              }`}
                                            />
                                            <BookOpen className="w-4 h-4 mr-2" />
                                            <div className="flex-1">
                                              <div className="font-medium text-sm">Chapter {chapter.id}</div>
                                              <div className="text-xs text-muted-foreground">{chapter.title}</div>
                                              {hasSections && (
                                                <div className="text-xs text-muted-foreground mt-1">
                                                  {chapter.sections.length} sections
                                                </div>
                                              )}
                                            </div>
                                          </Button>
                                        </CollapsibleTrigger>
                                        {hasSections && (
                                          <CollapsibleContent>
                                            <div className="ml-6 mt-1 space-y-1">
                                              {chapter.sections.map((sectionNum: string) => (
                                                <Button
                                                  key={sectionNum}
                                                  variant="ghost"
                                                  size="sm"
                                                  className="w-full justify-start text-left h-auto py-1.5"
                                                  onClick={() => {
                                                    setMpepSectionInput(sectionNum);
                                                    handleLoadMpepSection(sectionNum);
                                                  }}
                                                >
                                                  <FileText className="w-3 h-3 mr-2 flex-shrink-0" />
                                                  <div className="flex-1 text-xs">
                                                    MPEP § {sectionNum}
                                                  </div>
                                                </Button>
                                              ))}
                                            </div>
                                          </CollapsibleContent>
                                        )}
                                      </Collapsible>
                                    );
                                  });
                                })()}
                              </div>
                            </ScrollArea>
                          ) : (
                            <div className="text-center py-8 text-sm text-muted-foreground">
                              No MPEP structure available
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="cfr" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        {/* Direct section input */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Load Specific Section</label>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="Enter CFR citation (e.g., 1.111)"
                              className="flex-1"
                              value={cfrSectionInput}
                              onChange={(e) => setCfrSectionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleLoadCfrSection(cfrSectionInput);
                                }
                              }}
                            />
                            <Button
                              onClick={() => handleLoadCfrSection(cfrSectionInput)}
                              variant="default"
                              disabled={loadingCfrSection || !cfrSectionInput.trim()}
                            >
                              {loadingCfrSection ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                'Load'
                              )}
                            </Button>
                          </div>
                        </div>

                        <Separator />

                        {/* Browse CFR structure - simplified for now */}
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Browse 37 CFR</label>
                          {loadingCfrStructure ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                            </div>
                          ) : (
                            <div className="text-center py-8 text-sm text-muted-foreground">
                              Enter a CFR citation above to load it
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="usc" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <Input
                          type="text"
                          placeholder="Enter USC citation (e.g., 35 USC 103)"
                          className="w-full"
                        />
                        <Button className="w-full" variant="default">
                          Load USC Section
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Draft Selection Panel - Right Side Sliding Panel */}
      {showDraftPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setShowDraftPanel(false)}
          />

          {/* Sliding Panel */}
          <div className="fixed right-0 top-0 h-full w-[400px] bg-white shadow-2xl z-[70] transform transition-transform duration-300 ease-in-out">
            <div className="flex flex-col h-full">
              {/* Panel Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <FileEdit className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Select Draft</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setLoadingDrafts(true);
                      draftApi.listDrafts()
                        .then(response => {
                          setDrafts(response.drafts || []);
                          addNotification({
                            type: 'success',
                            title: 'Refreshed',
                            message: 'Drafts list updated',
                            duration: 2000
                          });
                        })
                        .catch(error => {
                          console.error('Failed to reload drafts:', error);
                          addNotification({
                            type: 'error',
                            title: 'Error',
                            message: 'Failed to refresh drafts',
                            duration: 3000
                          });
                        })
                        .finally(() => setLoadingDrafts(false));
                    }}
                    disabled={loadingDrafts}
                  >
                    <RefreshCw className={`w-5 h-5 ${loadingDrafts ? 'animate-spin' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDraftPanel(false)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {/* New Draft Button */}
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 mb-4 border-purple-200 hover:bg-purple-50"
                  onClick={async () => {
                    try {
                      const newDraft = await draftApi.createDraft('Untitled Draft', '', 'general');
                      setDrafts(prev => [newDraft, ...prev]);
                      setActiveDraft(newDraft);
                      setDraftContent('');
                      setDraftTitle('Untitled Draft');
                      setShowDraftPanel(false);
                      // Dispatch event to refresh drafts in other components
                      window.dispatchEvent(new Event('drafts-updated'));
                    } catch (error) {
                      console.error('Failed to create draft:', error);
                      addNotification({
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to create draft',
                        duration: 3000
                      });
                    }
                  }}
                >
                  <Plus className="w-4 h-4" />
                  New Draft
                </Button>

                {loadingDrafts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : drafts.length === 0 ? (
                  <div className="text-center py-8">
                    <FileEdit className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No drafts yet</p>
                    <p className="text-xs text-gray-400 mt-1">Create a draft to get started</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {drafts.map((draft) => (
                      <Button
                        key={draft.draft_id}
                        variant="ghost"
                        className={`w-full justify-start text-left h-auto py-3 px-3 ${
                          activeDraft?.draft_id === draft.draft_id ? 'bg-purple-50 border-purple-200 border' : ''
                        }`}
                        title={draft.title || 'Untitled'}
                        onClick={() => {
                          setActiveDraft(draft);
                          setDraftContent(draft.content || '');
                          setDraftTitle(draft.title);
                          setShowDraftPanel(false);
                        }}
                      >
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="font-medium text-sm truncate">{draft.title || 'Untitled'}</div>
                          <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {draft.content || 'Empty draft'}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {draft.draft_type || 'general'}
                            </Badge>
                            <span className="text-xs text-gray-400">
                              {new Date(draft.updated_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      </div>

      {/* Draft Viewer - Split View */}
      {activeDraft && (
        <div
          className={`bg-white ${hasOpenDocuments ? 'border-t' : 'border-l'} border-gray-200 shadow-xl flex flex-col relative overflow-hidden flex-shrink-0 ${hasOpenDocuments ? 'w-full' : 'max-w-[calc(100vw-700px)]'}`}
          style={hasOpenDocuments
            ? { height: `${draftWidth}px`, minHeight: '200px', maxHeight: '60vh' }
            : { width: `${draftWidth}px` }
          }
        >
          {/* Resize Handle */}
          <div
            className={hasOpenDocuments
              ? `absolute left-0 right-0 top-0 h-1.5 cursor-row-resize hover:bg-purple-300 transition-colors z-10 group ${isDraggingResize ? 'bg-purple-400' : ''}`
              : `absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-purple-300 transition-colors z-10 group ${isDraggingResize ? 'bg-purple-400' : ''}`
            }
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDraggingResize(true);
            }}
            title="Drag to resize draft panel"
          >
            <div className={hasOpenDocuments
              ? `absolute top-0 left-1/2 -translate-x-1/2 h-1 w-16 bg-gray-400 group-hover:bg-purple-500 rounded-b transition-colors ${isDraggingResize ? 'bg-purple-600' : ''}`
              : `absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-gray-400 group-hover:bg-purple-500 rounded-r transition-colors ${isDraggingResize ? 'bg-purple-600' : ''}`
            } />
          </div>
          <div className="flex flex-col h-full w-full overflow-x-auto">
            {/* Header */}
            <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0 bg-purple-50 w-full max-w-full overflow-hidden">
              <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                <FileEdit className="w-5 h-5 text-purple-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Input
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    className="text-sm font-semibold border-0 focus-visible:ring-0 px-0 bg-transparent"
                    placeholder="Draft title"
                  />
                  <div className="text-xs text-gray-500 mt-0.5">
                    Active Draft
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant={activeDraftsInChat.some(d => d.draft_id === activeDraft.draft_id) ? 'default' : 'outline'}
                  size="sm"
                  onClick={addDraftAsActiveDocument}
                  disabled={activeDraftsInChat.some(d => d.draft_id === activeDraft.draft_id)}
                  title={activeDraftsInChat.some(d => d.draft_id === activeDraft.draft_id) ? 'Already in Session documents' : 'Add to session'}
                  className="gap-1"
                >
                  <Plus className="w-4 h-4" />
                  {activeDraftsInChat.some(d => d.draft_id === activeDraft.draft_id) ? 'Active' : 'Add'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    setSavingDraft(true);
                    try {
                      await draftApi.updateDraft(activeDraft.draft_id, draftTitle, draftContent);

                      // Update active_drafts in localStorage if this draft is active
                      try {
                        const activeDraftsStr = localStorage.getItem('active_drafts');
                        if (activeDraftsStr) {
                          const activeDrafts = JSON.parse(activeDraftsStr);
                          const updatedDrafts = activeDrafts.map((d: any) =>
                            d.draft_id === activeDraft.draft_id
                              ? { ...d, title: draftTitle, content: draftContent }
                              : d
                          );
                          localStorage.setItem('active_drafts', JSON.stringify(updatedDrafts));

                          // Trigger storage update event to refresh UI
                          window.dispatchEvent(new Event('storage-update'));
                        }
                      } catch (error) {
                        console.error('Failed to update active drafts:', error);
                      }

                      addNotification({
                        type: 'success',
                        title: 'Saved',
                        message: 'Draft saved successfully',
                        duration: 2000
                      });
                      // Dispatch event to refresh drafts in other components
                      window.dispatchEvent(new Event('drafts-updated'));
                    } catch (error) {
                      console.error('Failed to save draft:', error);
                      addNotification({
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to save draft',
                        duration: 3000
                      });
                    } finally {
                      setSavingDraft(false);
                    }
                  }}
                  disabled={savingDraft}
                  title="Save draft"
                >
                  {savingDraft ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    try {
                      await exportDraftToDocx({
                        title: draftTitle || activeDraft.title || 'Untitled Draft',
                        content: draftContent,
                        draftType: activeDraft.draft_type,
                        metadata: {
                          updatedAt: activeDraft.updated_at,
                          createdAt: activeDraft.created_at,
                        },
                      });
                      addNotification({
                        type: 'success',
                        title: 'Exported',
                        message: 'Draft exported to DOCX successfully',
                        duration: 2000
                      });
                    } catch (error) {
                      console.error('Failed to export draft:', error);
                      addNotification({
                        type: 'error',
                        title: 'Export Failed',
                        message: 'Failed to export draft to DOCX',
                        duration: 3000
                      });
                    }
                  }}
                  title="Export to DOCX"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveDraft(null)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-x-auto overflow-y-auto p-4">
              {loadingDrafts ? (
                <div className="flex items-center justify-center min-h-[600px]">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                    <span className="text-sm text-gray-600">Loading draft...</span>
                  </div>
                </div>
              ) : (
                <LineNumberedTextarea
                  value={draftContent}
                  onChange={(value) => setDraftContent(value)}
                  selectedLine={selectedDraftLine}
                  onLineSelect={setSelectedDraftLine}
                  className="w-full h-full max-w-full resize-none border-0 focus-visible:ring-0 font-mono text-sm"
                  placeholder="Start writing your draft here..."
                />
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-3">
                  <span>{draftContent.length} characters</span>
                  <span>{draftContent.split(/\s+/).filter(w => w.length > 0).length} words</span>
                </div>
                <Badge variant="outline" className="text-xs">
                  {activeDraft.draft_type || 'general'}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
